import { Injectable, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { collection, doc, getDoc, getDocs, setDoc, addDoc, deleteDoc, query, orderBy, limit, onSnapshot, writeBatch, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { Storage, ref, uploadBytes, listAll, getDownloadURL, deleteObject, getMetadata } from '@angular/fire/storage';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Auth } from '@angular/fire/auth';
import { AppReleaseTargets, partitionAppReleaseTargets } from '../utils/app-release-targets.util';
import {
  StorageDeleteOutcome,
  combineDeleteOutcomes,
  isOwnedStoragePath,
  storagePathFromDownloadUrl
} from '../utils/storage-ownership.util';
import { CUSTOMIZABLE_OFF_MESSAGES, isStandaloneDevice, planCustomizableCleanup } from '../utils/customizable-cleanup.util';
import { CloneTargetState } from '../utils/clone-messages.util';
import {
  STANDALONE_HOUSEKEEPING_MESSAGES,
  filterFrameGroupDocsForTheme,
  hasStandaloneCanvas,
  resolveFrameAnchor
} from '../utils/standalone-housekeeping.util';

export interface CustomizableCleanupResult {
  /** Inheriting devices whose customization of the tile(s) was removed. */
  cleaned: string[];
  /** Standalone devices that were deliberately not touched. */
  skippedStandalone: string[];
  /** Devices where the cleanup failed (config is consistent; some storage files may remain). */
  failed: { deviceId: string; error: string }[];
}

export interface CustomizableOffUi {
  confirm: (message: string) => boolean;
  alert: (message: string) => void;
}

export interface ThemeFrame {
  id: string;
  name: string;
  description: string;
  uploadedFrames?: any[];
  orderIndex?: number;
}

export interface MachineStatusEmailConfig {
  sendMachineStatusEmail: boolean;
  machineStatusEmailRecipients: string;
  machineStatusEmailHourPT: number;
  updatedAt?: any;
  updatedBy?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PhotoboothService {
  private firestore = inject(Firestore);
  private storage = inject(Storage);
  private functions = inject(Functions);
  private auth = inject(Auth);
  private unsavedLayoutDevices = new Set<string>();

  /** KAN-523 AC-11: tracks whether a device has unsaved layout edits in the editor. */
  setHasUnsavedLayoutChanges(deviceId: string, dirty: boolean): void {
    if (!deviceId || deviceId === 'global') return;
    if (dirty) {
      this.unsavedLayoutDevices.add(deviceId);
    } else {
      this.unsavedLayoutDevices.delete(deviceId);
    }
  }

  /** KAN-523 AC-11: true when the device layout editor currently has unsaved changes. */
  hasUnsavedLayoutChanges(deviceId: string): boolean {
    if (!deviceId || deviceId === 'global') return false;
    return this.unsavedLayoutDevices.has(deviceId);
  }

  async getCutStyles(): Promise<string[]> {
    const cutStylesRef = collection(this.firestore, 'Photo_cutstyle');
    const snapshot = await getDocs(cutStylesRef);
    if (snapshot.empty) {
      await setDoc(doc(cutStylesRef, 'Strip'), { name: 'Strip' });
      await setDoc(doc(cutStylesRef, 'Multi'), { name: 'Multi' });
      return ['Strip', 'Multi'];
    }
    const styles = snapshot.docs.map(d => d.id);
    const stripStyle = styles.find(s => s.toLowerCase() === 'strip');
    if (stripStyle) {
      return [stripStyle, ...styles.filter(s => s !== stripStyle)];
    }
    return styles;
  }

  // --- Layout Methods ---

  async getGlobalLayout(version?: string | null): Promise<any[]> {
    const path = version
      ? `photobooth_config_published/${version}/config/global`
      : 'photobooth_config/global';
    const globalDocRef = doc(this.firestore, path);
    const docSnap = await getDoc(globalDocRef);
    if (docSnap.exists()) {
      return docSnap.data()['layout']?.['tiles'] || docSnap.data()['tiles'] || [];
    }
    return [];
  }

  async getDeviceLayout(deviceId: string, version?: string | null): Promise<any[]> {
    const path = version
      ? `photobooth_config_published/${version}/config/${deviceId}`
      : `photobooth_config/${deviceId}`;
    const deviceDocRef = doc(this.firestore, path);
    const docSnap = await getDoc(deviceDocRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data['overrideGlobalLayout'] === true) {
        return data['standaloneTiles'] || data['layout']?.['tiles'] || data['tiles'] || [];
      }
      const customTiles = data['customizedTiles'] || data['layout']?.['tiles'] || data['tiles'] || [];
      if (customTiles.length > 0) {
        return customTiles;
      }
    }
    // Fallback to global
    return this.getGlobalLayout(version);
  }

  async getDeviceOverrideGlobalLayout(deviceId: string, version?: string | null): Promise<boolean> {
    if (!deviceId || deviceId === 'global') return false;
    const path = version
      ? `photobooth_config_published/${version}/config/${deviceId}`
      : `photobooth_config/${deviceId}`;
    const deviceDocRef = doc(this.firestore, path);
    const docSnap = await getDoc(deviceDocRef);
    if (docSnap.exists()) {
      return docSnap.data()['overrideGlobalLayout'] === true;
    }
    return false;
  }

  async setDeviceOverrideGlobalLayout(deviceId: string, override: boolean): Promise<void> {
    if (!deviceId || deviceId === 'global') return;
    const deviceDocRef = doc(this.firestore, `photobooth_config/${deviceId}`);
    await setDoc(deviceDocRef, {
      overrideGlobalLayout: override,
      updatedAt: new Date()
    }, { merge: true });

    // Also update devices collection
    const devRef = doc(this.firestore, `devices/${deviceId}`);
    await setDoc(devRef, {
      overrideGlobalLayout: override
    }, { merge: true });
  }

  /** KAN-518: `confirmReplace` must be true when the target already has layout or frame group data (the user confirmed "Replace layout"). */
  async cloneDeviceConfiguration(sourceDeviceId: string, targetDeviceId: string, confirmReplace: boolean = false): Promise<any> {
    const cloneFn = httpsCallable(this.functions, 'cloneDeviceConfiguration');
    const res = await cloneFn({ sourceDeviceId, targetDeviceId, confirmReplace });
    return res.data;
  }

  /** KAN-518: what the target already holds (no writes), used to word the clone confirmation. */
  async inspectCloneTarget(sourceDeviceId: string, targetDeviceId: string): Promise<CloneTargetState> {
    const cloneFn = httpsCallable(this.functions, 'cloneDeviceConfiguration');
    const res: any = await cloneFn({ sourceDeviceId, targetDeviceId, inspectOnly: true });
    return res.data?.existing || { hasData: false, frameGroups: 0, files: 0 };
  }

  async checkIfDeviceCustomized(deviceId: string, version?: string | null): Promise<boolean> {
    const path = version
      ? `photobooth_config_published/${version}/config/${deviceId}`
      : `photobooth_config/${deviceId}`;
    const deviceDocRef = doc(this.firestore, path);
    const docSnap = await getDoc(deviceDocRef);
    if (docSnap.exists()) {
      return true;
    }
    return false;
  }

  /** KAN-523 AC-7: true only when `photobooth_config/{deviceId}` holds a standalone canvas (`standaloneTiles` array exists). */
  async hasDeviceStandaloneCanvas(deviceId: string, version?: string | null): Promise<boolean> {
    if (!deviceId || deviceId === 'global') return false;
    const path = version
      ? `photobooth_config_published/${version}/config/${deviceId}`
      : `photobooth_config/${deviceId}`;
    const deviceDocRef = doc(this.firestore, path);
    const docSnap = await getDoc(deviceDocRef);
    return docSnap.exists() && hasStandaloneCanvas(docSnap.data());
  }

  async checkIfDevicePublishedInVersion(deviceId: string, version: string): Promise<boolean> {
    const path = `photobooth_config_published/${version}/config/${deviceId}`;
    const deviceDocRef = doc(this.firestore, path);
    const docSnap = await getDoc(deviceDocRef);
    return docSnap.exists();
  }

  async saveGlobalLayout(tiles: any[]): Promise<void> {
    const globalDocRef = doc(this.firestore, 'photobooth_config', 'global');
    await setDoc(globalDocRef, {
      tiles: tiles,
      layout: { tiles: tiles },
      updatedAt: new Date()
    }, { merge: true });
  }

  async saveDeviceLayout(deviceId: string, tiles: any[], overrideGlobalLayout?: boolean): Promise<void> {
    const deviceDocRef = doc(this.firestore, 'photobooth_config', deviceId);
    const updatePayload: any = {
      tiles: tiles,
      layout: { tiles: tiles },
      updatedAt: new Date()
    };
    if (overrideGlobalLayout === true) {
      updatePayload.overrideGlobalLayout = true;
      updatePayload.standaloneTiles = tiles;
    } else if (overrideGlobalLayout === false) {
      updatePayload.overrideGlobalLayout = false;
      updatePayload.customizedTiles = tiles;
    }
    await setDoc(deviceDocRef, updatePayload, { merge: true });

    if (overrideGlobalLayout !== undefined) {
      await setDoc(doc(this.firestore, `devices/${deviceId}`), {
        overrideGlobalLayout: overrideGlobalLayout
      }, { merge: true });
    }
    this.setHasUnsavedLayoutChanges(deviceId, false);
  }

  /**
   * KAN-523 AC-5: Deletes `photobooth_config/{deviceId}`, all its `frame_groups`, `Photobooth/{deviceId}/Assets` and `Videos`,
   * and sets `overrideGlobalLayout: false, isCustomized: false` on `devices/{deviceId}`. Never touches published copies or global.
   */
  async revertDeviceToGlobal(deviceId: string): Promise<void> {
    if (!deviceId || deviceId === 'global') {
      throw new Error('Cannot revert global configuration.');
    }

    // 1. Delete all frame_groups subcollection documents for this device
    const framesSnap = await getDocs(collection(this.firestore, `photobooth_config/${deviceId}/frame_groups`));
    await Promise.all(framesSnap.docs.map(d => deleteDoc(d.ref)));

    // 2. Delete the device config document
    const deviceDocRef = doc(this.firestore, 'photobooth_config', deviceId);
    await deleteDoc(deviceDocRef);

    // 3. Reset flags on devices/{deviceId}
    await setDoc(doc(this.firestore, `devices/${deviceId}`), {
      overrideGlobalLayout: false,
      isCustomized: false
    }, { merge: true });

    // 4. Delete custom Assets and Videos from Storage (owned paths only; never Published or Sessions)
    for (const folder of ['Assets', 'Videos']) {
      const storagePath = `Photobooth/${deviceId}/${folder}`;
      if (isOwnedStoragePath(`${storagePath}/x`, false, deviceId)) {
        await this.deleteStorageDirectory(storagePath);
      }
    }

    this.setHasUnsavedLayoutChanges(deviceId, false);
  }

  /**
   * KAN-523 AC-3: Counts and lists the `frame_groups` docs and Storage files under `Photobooth/{deviceId}/Assets/{tileId}/**`
   * belonging to a standalone tile (using longest-prefix matching across known layout theme IDs, AC-10).
   */
  async inspectStandaloneTileAssets(
    deviceId: string,
    tileId: string,
    knownThemeIds: string[] = []
  ): Promise<{ frameGroups: number; files: number; groupDocIds: string[]; filePaths: string[] }> {
    if (!deviceId || deviceId === 'global' || !tileId) {
      return { frameGroups: 0, files: 0, groupDocIds: [], filePaths: [] };
    }

    let themes = [...knownThemeIds].filter(Boolean);
    if (themes.length === 0) {
      const layout = await this.getDeviceLayout(deviceId);
      themes = (layout || []).map((t: any) => t?.id).filter(Boolean);
    }

    const framesSnap = await getDocs(collection(this.firestore, `photobooth_config/${deviceId}/frame_groups`));
    const matchedGroups = filterFrameGroupDocsForTheme(framesSnap.docs, tileId, themes);
    const groupDocIds = matchedGroups.map(m => m.doc.id);

    const tileStorageDir = `Photobooth/${deviceId}/Assets/${tileId}`;
    const rawFiles = isOwnedStoragePath(`${tileStorageDir}/x`, false, deviceId)
      ? await this.listStorageFilesRecursively(tileStorageDir)
      : [];
    const filePaths = rawFiles.filter(p => isOwnedStoragePath(p, false, deviceId));

    return {
      frameGroups: groupDocIds.length,
      files: filePaths.length,
      groupDocIds,
      filePaths
    };
  }

  /**
   * KAN-523 AC-3: Deletes a standalone tile's `frame_groups/{tileId}_*` docs and `Photobooth/{deviceId}/Assets/{tileId}/**` files,
   * and nothing else.
   */
  async deleteStandaloneTileAssets(
    deviceId: string,
    tileId: string,
    knownThemeIds: string[] = []
  ): Promise<{ deletedFrameGroups: number; deletedFiles: number }> {
    if (!deviceId || deviceId === 'global' || !tileId) {
      throw new Error('Cannot delete standalone tile assets for global or empty deviceId.');
    }
    const inspected = await this.inspectStandaloneTileAssets(deviceId, tileId, knownThemeIds);
    await Promise.all(
      inspected.groupDocIds.map(docId =>
        deleteDoc(doc(this.firestore, `photobooth_config/${deviceId}/frame_groups/${docId}`))
      )
    );
    await Promise.all(
      inspected.filePaths.map(filePath => this.deleteOwnedObject(false, deviceId, filePath))
    );
    return {
      deletedFrameGroups: inspected.groupDocIds.length,
      deletedFiles: inspected.filePaths.length
    };
  }

  // --- Video Configuration ---

  async getVideos(isGlobal: boolean, deviceId: string, type: 'intro' | 'printing', version?: string | null): Promise<string[]> {
    const path = version
      ? (isGlobal ? `photobooth_config_published/${version}/config/global` : `photobooth_config_published/${version}/config/${deviceId}`)
      : (isGlobal ? 'photobooth_config/global' : `photobooth_config/${deviceId}`);

    const docRef = doc(this.firestore, path);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data()[`${type}Videos`] || [];
    }
    return [];
  }

  async getExcludeGlobalVideos(isGlobal: boolean, deviceId: string, version?: string | null): Promise<boolean> {
    if (isGlobal) return false;
    const path = version
      ? `photobooth_config_published/${version}/config/${deviceId}`
      : `photobooth_config/${deviceId}`;

    const docRef = doc(this.firestore, path);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data()['excludeGlobalVideos'] === true;
    }
    return false;
  }

  async setExcludeGlobalVideos(deviceId: string, exclude: boolean): Promise<void> {
    const docPath = `photobooth_config/${deviceId}`;
    const docRef = doc(this.firestore, docPath);
    await setDoc(docRef, { excludeGlobalVideos: exclude }, { merge: true });
  }

  async uploadVideo(isGlobal: boolean, deviceId: string, type: 'intro' | 'printing', file: File): Promise<string> {
    const fileName = `${Date.now()}_${file.name}`;
    const storagePath = isGlobal
      ? `Photobooth/global/Videos/${type}/${fileName}`
      : `Photobooth/${deviceId}/Videos/${type}/${fileName}`;

    const storageRef = ref(this.storage, storagePath);
    await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(storageRef);

    // Save to Firestore array
    const docPath = isGlobal ? 'photobooth_config/global' : `photobooth_config/${deviceId}`;
    const docRef = doc(this.firestore, docPath);
    const docSnap = await getDoc(docRef);

    let videos = [];
    if (docSnap.exists()) {
      videos = docSnap.data()[`${type}Videos`] || [];
    }
    videos.push(downloadUrl);

    await setDoc(docRef, { [`${type}Videos`]: videos }, { merge: true });
    return downloadUrl;
  }

  async uploadPrintingMediaItem(isGlobal: boolean, deviceId: string, videoFile: File, bgFile?: File | null): Promise<any> {
    const timestamp = Date.now();
    const cleanVideoBase = videoFile.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_');
    const videoFileName = `${timestamp}_${cleanVideoBase}.mp4`;
    const videoStoragePath = isGlobal
      ? `Photobooth/global/Videos/printing/${videoFileName}`
      : `Photobooth/${deviceId}/Videos/printing/${videoFileName}`;

    const videoRef = ref(this.storage, videoStoragePath);
    await uploadBytes(videoRef, videoFile);
    const videoUrl = await getDownloadURL(videoRef);

    let bgUrl: string | undefined = undefined;
    let bgFileName: string | undefined = undefined;

    if (bgFile) {
      const bgExt = bgFile.name.split('.').pop() || 'png';
      bgFileName = `${timestamp}_${cleanVideoBase}_PBG.${bgExt}`;
      const bgStoragePath = isGlobal
        ? `Photobooth/global/Videos/printing/${bgFileName}`
        : `Photobooth/${deviceId}/Videos/printing/${bgFileName}`;

      const bgRef = ref(this.storage, bgStoragePath);
      await uploadBytes(bgRef, bgFile);
      bgUrl = await getDownloadURL(bgRef);
    }

    const docPath = isGlobal ? 'photobooth_config/global' : `photobooth_config/${deviceId}`;
    const docRef = doc(this.firestore, docPath);
    const docSnap = await getDoc(docRef);

    let printingVideos: any[] = [];
    if (docSnap.exists()) {
      printingVideos = docSnap.data()['printingVideos'] || [];
    }

    const newMediaItem: any = {
      videoUrl: videoUrl,
      videoName: videoFileName
    };
    if (bgUrl) {
      newMediaItem.backgroundUrl = bgUrl;
      newMediaItem.backgroundName = bgFileName;
    }

    printingVideos.push(newMediaItem);
    await setDoc(docRef, { printingVideos }, { merge: true });
    return newMediaItem;
  }

  async updatePrintingMediaItemBg(isGlobal: boolean, deviceId: string, videoUrl: string, bgFile: File): Promise<string> {
    const docPath = isGlobal ? 'photobooth_config/global' : `photobooth_config/${deviceId}`;
    const docRef = doc(this.firestore, docPath);
    const docSnap = await getDoc(docRef);

    let printingVideos: any[] = [];
    if (docSnap.exists()) {
      printingVideos = docSnap.data()['printingVideos'] || [];
    }

    const itemIndex = printingVideos.findIndex((v: any) => (typeof v === 'string' ? v : v.videoUrl) === videoUrl);
    if (itemIndex === -1) throw new Error('Printing video item not found');

    const item = typeof printingVideos[itemIndex] === 'string' ? { videoUrl: printingVideos[itemIndex] } : { ...printingVideos[itemIndex] };

    // Delete existing bg only if this scope owns it (a global/foreign backdrop keeps its object; the reference is replaced below)
    if (item.backgroundUrl) {
      await this.deleteOwnedObjectByUrl(isGlobal, deviceId, item.backgroundUrl);
    }

    // Extract clean name from videoUrl
    let cleanBase = 'printing';
    try {
      const urlObj = new URL(videoUrl);
      const filename = decodeURIComponent(urlObj.pathname).split('/').pop() || 'printing';
      cleanBase = filename.replace(/\.[^/.]+$/, '');
    } catch { }

    const bgExt = bgFile.name.split('.').pop() || 'png';
    const bgFileName = `${cleanBase}_PBG.${bgExt}`;
    const bgStoragePath = isGlobal
      ? `Photobooth/global/Videos/printing/${bgFileName}`
      : `Photobooth/${deviceId}/Videos/printing/${bgFileName}`;

    const bgRef = ref(this.storage, bgStoragePath);
    await uploadBytes(bgRef, bgFile);
    const bgUrl = await getDownloadURL(bgRef);

    item.backgroundUrl = bgUrl;
    item.backgroundName = bgFileName;
    printingVideos[itemIndex] = item;

    await setDoc(docRef, { printingVideos }, { merge: true });
    return bgUrl;
  }

  async removePrintingMediaItemBg(isGlobal: boolean, deviceId: string, videoUrl: string): Promise<StorageDeleteOutcome> {
    let outcome: StorageDeleteOutcome = 'deleted';
    const docPath = isGlobal ? 'photobooth_config/global' : `photobooth_config/${deviceId}`;
    const docRef = doc(this.firestore, docPath);
    const docSnap = await getDoc(docRef);

    let printingVideos: any[] = [];
    if (docSnap.exists()) {
      printingVideos = docSnap.data()['printingVideos'] || [];
    }

    const itemIndex = printingVideos.findIndex((v: any) => (typeof v === 'string' ? v : v.videoUrl) === videoUrl);
    if (itemIndex === -1) return outcome;

    const item = typeof printingVideos[itemIndex] === 'string' ? { videoUrl: printingVideos[itemIndex] } : { ...printingVideos[itemIndex] };

    if (item.backgroundUrl) {
      outcome = await this.deleteOwnedObjectByUrl(isGlobal, deviceId, item.backgroundUrl);
      delete item.backgroundUrl;
      delete item.backgroundName;
      printingVideos[itemIndex] = item;
      await setDoc(docRef, { printingVideos }, { merge: true });
    }
    return outcome;
  }

  /**
   * Removes a video (and its coupled backdrop) reference from this scope's config and deletes only the Storage
   * objects this scope owns. A URL pointing at the global library or another device removes the reference only (KAN-517).
   */
  async deleteVideo(isGlobal: boolean, deviceId: string, type: 'intro' | 'printing', videoUrl: string): Promise<StorageDeleteOutcome> {
    // Remove from Firestore
    const docPath = isGlobal ? 'photobooth_config/global' : `photobooth_config/${deviceId}`;
    const docRef = doc(this.firestore, docPath);
    const docSnap = await getDoc(docRef);

    let removedItem: any = null;
    if (docSnap.exists()) {
      let videos: any[] = docSnap.data()[`${type}Videos`] || [];
      removedItem = videos.find((v: any) => (typeof v === 'string' ? v : v.videoUrl) === videoUrl);
      videos = videos.filter((v: any) => (typeof v === 'string' ? v : v.videoUrl) !== videoUrl);
      await setDoc(docRef, { [`${type}Videos`]: videos }, { merge: true });
    }

    const outcomes: StorageDeleteOutcome[] = [await this.deleteOwnedObjectByUrl(isGlobal, deviceId, videoUrl)];

    // If it had a coupled background image, it follows the same rule
    if (removedItem && typeof removedItem === 'object' && removedItem.backgroundUrl) {
      outcomes.push(await this.deleteOwnedObjectByUrl(isGlobal, deviceId, removedItem.backgroundUrl));
    }
    return combineDeleteOutcomes(outcomes);
  }

  /**
   * The ONE guard for Storage deletes issued by device actions (KAN-517): deletes the object only when its path is
   * under this scope's own folder (Photobooth/{deviceId}/, or Photobooth/global/ in the global editor). Otherwise nothing
   * is deleted and 'reference-only' is returned so the caller removes just the Firestore reference.
   */
  private async deleteOwnedObject(isGlobal: boolean, deviceId: string, fullPath: string | null): Promise<StorageDeleteOutcome> {
    if (!isOwnedStoragePath(fullPath, isGlobal, deviceId)) return 'reference-only';
    await deleteObject(ref(this.storage, fullPath as string));
    return 'deleted';
  }

  /** Same guard for a stored download URL. Storage errors are logged, not thrown, so the reference removal still goes on. */
  private async deleteOwnedObjectByUrl(isGlobal: boolean, deviceId: string, url: string): Promise<StorageDeleteOutcome> {
    try {
      return await this.deleteOwnedObject(isGlobal, deviceId, storagePathFromDownloadUrl(url));
    } catch (e) {
      console.warn('Could not delete file from storage:', e);
      return 'deleted';
    }
  }

  /** Standalone when EITHER photobooth_config/{id} or devices/{id} says overrideGlobalLayout === true. */
  private async isStandaloneByFlags(deviceId: string): Promise<boolean> {
    if (!deviceId || deviceId === 'global') return false;
    const [cfg, dev] = await Promise.all([
      getDoc(doc(this.firestore, `photobooth_config/${deviceId}`)),
      getDoc(doc(this.firestore, `devices/${deviceId}`))
    ]);
    return isStandaloneDevice(cfg.exists() ? cfg.data() : null, dev.exists() ? dev.data() : null);
  }

  /**
   * Read-only plan for "turn customizable off" (KAN-519): which devices would be changed. Standalone devices
   * (either flag) are never planned. Devices are the `devices` collection, as before.
   */
  private async planCustomizableOff(themeIds: string[]): Promise<{
    inheriting: { deviceId: string; fields: Record<string, any[]>; tileIds: string[] }[];
    standalone: string[];
  }> {
    const inheriting: { deviceId: string; fields: Record<string, any[]>; tileIds: string[] }[] = [];
    const standalone: string[] = [];
    if (!themeIds || themeIds.length === 0) return { inheriting, standalone };

    const devicesSnap = await getDocs(collection(this.firestore, 'devices'));
    for (const devDoc of devicesSnap.docs) {
      const deviceId = devDoc.id;
      if (deviceId === 'global') continue;
      const cfgSnap = await getDoc(doc(this.firestore, `photobooth_config/${deviceId}`));
      const cfgData = cfgSnap.exists() ? cfgSnap.data() : null;
      if (isStandaloneDevice(cfgData, devDoc.data())) {
        standalone.push(deviceId);
        continue;
      }
      const patch = planCustomizableCleanup(cfgData, themeIds);
      if (patch) inheriting.push({ deviceId, fields: patch.fields, tileIds: patch.tileIds });
    }
    return { inheriting, standalone };
  }

  /** Counts for the confirmation: inheriting devices that would lose their customization, and standalone ones that are left alone. */
  async countCustomizableCleanupTargets(themeIds: string[]): Promise<{ inheriting: number; standalone: number }> {
    const plan = await this.planCustomizableOff(themeIds);
    return { inheriting: plan.inheriting.length, standalone: plan.standalone.length };
  }

  /**
   * Removes inheriting devices' customization of the given global tiles (KAN-519). Standalone devices are skipped
   * entirely; for inheriting devices the config is updated field-by-field and the device's frame groups for that tile
   * are removed in the SAME atomic batch, then its Storage files for that tile are deleted. One failing device is
   * reported and does not stop the others.
   */
  async cleanupDisabledCustomizableTiles(themeIds: string[]): Promise<CustomizableCleanupResult> {
    const result: CustomizableCleanupResult = { cleaned: [], skippedStandalone: [], failed: [] };
    if (!themeIds || themeIds.length === 0) return result;

    const plan = await this.planCustomizableOff(themeIds);
    result.skippedStandalone = plan.standalone;

    for (const target of plan.inheriting) {
      const { deviceId, fields, tileIds } = target;
      try {
        const framesSnap = await getDocs(collection(this.firestore, `photobooth_config/${deviceId}/frame_groups`));
        const batch = writeBatch(this.firestore);
        batch.update(doc(this.firestore, `photobooth_config/${deviceId}`), { ...fields, updatedAt: new Date() });
        framesSnap.docs.forEach(fDoc => {
          if (tileIds.some(tid => fDoc.id.startsWith(`${tid}_`))) batch.delete(fDoc.ref);
        });
        await batch.commit();

        for (const tid of tileIds) {
          await this.deleteStorageDirectory(`Photobooth/${deviceId}/Assets/${tid}`);
          for (const suffix of ['_TTB', '_TBG']) {
            try {
              await deleteObject(ref(this.storage, `Photobooth/${deviceId}/Assets/${tid}${suffix}.png`));
            } catch (e: any) {
              if (e?.code !== 'storage/object-not-found') throw e;
            }
          }
        }
        result.cleaned.push(deviceId);
      } catch (e: any) {
        console.error(`Customizable cleanup failed for ${deviceId}`, e);
        result.failed.push({ deviceId, error: e?.message || String(e) });
      }
    }
    return result;
  }

  /**
   * Saves the GLOBAL layout. When customizable switches were turned off, asks first (count of inheriting machines),
   * saves, cleans inheriting devices only, and reports the result (KAN-519). `ui` is injected so the flow is testable.
   */
  async saveGlobalLayoutWithCustomizableOff(tiles: any[], disabledThemeIds: string[], ui: CustomizableOffUi): Promise<'saved' | 'cancelled'> {
    if (!disabledThemeIds || disabledThemeIds.length === 0) {
      await this.saveGlobalLayout(tiles);
      return 'saved';
    }
    const { inheriting } = await this.countCustomizableCleanupTargets(disabledThemeIds);
    if (!ui.confirm(CUSTOMIZABLE_OFF_MESSAGES.confirm(inheriting))) return 'cancelled';

    await this.saveGlobalLayout(tiles);
    const result = await this.cleanupDisabledCustomizableTiles(disabledThemeIds);
    ui.alert(
      result.failed.length > 0
        ? CUSTOMIZABLE_OFF_MESSAGES.partialFailure(result.failed.length, result.failed.map(f => f.deviceId))
        : CUSTOMIZABLE_OFF_MESSAGES.saved(result.cleaned.length)
    );
    return 'saved';
  }

  async getPingInterval(): Promise<number> {
    const globalDocRef = doc(this.firestore, 'photobooth_config/global');
    const docSnap = await getDoc(globalDocRef);
    if (docSnap.exists() && docSnap.data()['pingInterval'] !== undefined) {
      return docSnap.data()['pingInterval'];
    }
    return 15; // default 15 minutes
  }

  async setPingInterval(minutes: number): Promise<void> {
    const globalDocRef = doc(this.firestore, 'photobooth_config/global');
    await setDoc(globalDocRef, { pingInterval: minutes }, { merge: true });
  }

  // --- Machine Status Email Notification Methods ---

  async getMachineStatusEmailConfig(): Promise<MachineStatusEmailConfig> {
    const globalDocRef = doc(this.firestore, 'photobooth_config/global');
    const docSnap = await getDoc(globalDocRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        sendMachineStatusEmail: data['sendMachineStatusEmail'] === true,
        machineStatusEmailRecipients: data['machineStatusEmailRecipients'] || '',
        machineStatusEmailHourPT: data['machineStatusEmailHourPT'] !== undefined ? Number(data['machineStatusEmailHourPT']) : 8,
        updatedAt: data['machineStatusEmailUpdatedAt'],
        updatedBy: data['machineStatusEmailUpdatedBy']
      };
    }
    return {
      sendMachineStatusEmail: false,
      machineStatusEmailRecipients: '',
      machineStatusEmailHourPT: 8
    };
  }

  async saveMachineStatusEmailConfig(config: Partial<MachineStatusEmailConfig>): Promise<void> {
    const globalDocRef = doc(this.firestore, 'photobooth_config/global');
    const userEmail = this.auth.currentUser?.email || 'unknown';
    const payload: any = {
      sendMachineStatusEmail: !!config.sendMachineStatusEmail,
      machineStatusEmailRecipients: config.machineStatusEmailRecipients || '',
      machineStatusEmailHourPT: config.machineStatusEmailHourPT !== undefined ? Number(config.machineStatusEmailHourPT) : 8,
      machineStatusEmailUpdatedAt: new Date(),
      machineStatusEmailUpdatedBy: userEmail
    };
    await setDoc(globalDocRef, payload, { merge: true });
  }

  async sendMachineStatusEmailTest(emails: string[]): Promise<any> {
    try {
      const sendEmailFn = httpsCallable<any, any>(this.functions, 'sendMachineStatusEmailNow');
      const res = await sendEmailFn({ emails });
      return res.data;
    } catch (fnErr: any) {
      console.warn('sendMachineStatusEmailNow callable failed, falling back to direct email receipt generation:', fnErr);
      return await this.sendMachineStatusEmailDirectFallback(emails);
    }
  }

  private async sendMachineStatusEmailDirectFallback(emails: string[]): Promise<any> {
    const globalDocRef = doc(this.firestore, 'photobooth_config/global');
    const globalSnap = await getDoc(globalDocRef);
    const pingInterval = globalSnap.exists() && globalSnap.data()['pingInterval'] ? globalSnap.data()['pingInterval'] : 15;
    const pingIntervalMs = pingInterval * 60 * 1000;
    const now = Date.now();

    const [devicesSnap, storesSnap] = await Promise.all([
      getDocs(collection(this.firestore, 'devices')),
      getDocs(collection(this.firestore, 'stores'))
    ]);

    const storeMap = new Map<string, any>();
    storesSnap.docs.forEach(d => {
      storeMap.set(d.id, d.data());
      if (d.data()['storeId']) storeMap.set(d.data()['storeId'], d.data());
    });

    const machinesList: any[] = [];
    devicesSnap.docs.forEach(d => {
      const dev = d.data();
      const devId = dev['deviceId'] || dev['id'] || d.id;
      if (devId === 'global' || dev['isDeleted'] === true || dev['isTesting'] === true) return;

      let lastSeen: Date | null = null;
      if (dev['lastSeen']) {
        if (typeof dev['lastSeen'].toDate === 'function') {
          const d = dev['lastSeen'].toDate();
          if (d instanceof Date && !isNaN(d.getTime())) lastSeen = d;
        } else if (dev['lastSeen'] instanceof Date && !isNaN(dev['lastSeen'].getTime())) {
          lastSeen = dev['lastSeen'];
        } else if (typeof dev['lastSeen'] === 'number' || typeof dev['lastSeen'] === 'string') {
          const d = new Date(dev['lastSeen']);
          if (!isNaN(d.getTime())) lastSeen = d;
        }
      }

      let isOnline = false;
      if (lastSeen) {
        isOnline = (now - lastSeen.getTime()) <= pingIntervalMs && dev['deviceOnline'] !== false;
      }

      const store = dev['storeId'] ? storeMap.get(dev['storeId']) : null;
      const storeName = store ? (store['name'] || store['storeId'] || dev['storeId']) : (dev['storeId'] || 'Unassigned');
      const storeLoc = store?.['location'] || 'N/A';

      let lastSeenStr = 'N/A';
      if (lastSeen) {
        try {
          lastSeenStr = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Los_Angeles',
            dateStyle: 'short',
            timeStyle: 'short'
          }).format(lastSeen) + ' PT';
        } catch (err) {
          lastSeenStr = 'N/A';
        }
      }

      const cameraConnected = isOnline && dev['cameraConnected'] === true;
      const useCard = dev['useCard'] !== false;
      const cardConnected = useCard ? (isOnline && dev['cardMachineConnected'] === true) : null;
      const useCash = dev['useCash'] !== false;
      const cashConnected = useCash ? (isOnline && dev['billAcceptorConnected'] === true) : null;
      const isInMaintenance = dev['isInMaintenance'] === true;

      let overallStatus: 'offline' | 'warning' | 'online' = 'offline';
      if (!isOnline) {
        overallStatus = 'offline';
      } else {
        const allPeripheralsWorking = !isInMaintenance &&
          cameraConnected &&
          (useCard ? cardConnected === true : true) &&
          (useCash ? cashConnected === true : true);
        overallStatus = allPeripheralsWorking ? 'online' : 'warning';
      }

      const remainingPapers = dev['RemainingPapers'] !== undefined
        ? dev['RemainingPapers']
        : (dev['remainingPapers'] !== undefined ? dev['remainingPapers'] : undefined);

      machinesList.push({
        deviceId: devId,
        deviceName: dev['deviceName'] || dev['name'] || devId,
        storeName: `${storeName} (${storeLoc})`,
        isOnline: isOnline,
        overallStatus: overallStatus,
        isInMaintenance: isInMaintenance,
        cameraConnected: cameraConnected,
        useCard: useCard,
        cardConnected: cardConnected,
        useCash: useCash,
        cashConnected: cashConnected,
        remainingPapers: remainingPapers,
        lastSeenStr: lastSeenStr,
        isTesting: dev['isTesting'] === true
      });
    });

    const statusRank = (status: string) => {
      if (status === 'offline') return 0; // Gray
      if (status === 'warning') return 1; // Yellow
      return 2; // Green
    };

    machinesList.sort((a, b) => {
      const rankA = statusRank(a.overallStatus);
      const rankB = statusRank(b.overallStatus);
      if (rankA !== rankB) {
        return rankA - rankB; // Gray (0) -> Yellow (1) -> Green (2)
      }
      return a.deviceId.localeCompare(b.deviceId);
    });

    const htmlContent = this.generateMachineStatusEmailHtml(machinesList);
    const textContent = `Photobooth Fleet Status Report - Total: ${machinesList.length} machines.`;

    const userEmail = this.auth.currentUser?.email || 'unknown';
    const emailData = {
      senderType: 'accounting',
      fromName: 'Photobooth System Monitor',
      to: emails,
      subject: `[Photobooth Fleet Status] Daily Machine Health Report (${new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', dateStyle: 'medium' }).format(new Date())})`,
      message: {
        subject: `[Photobooth Fleet Status] Daily Machine Health Report`,
        html: htmlContent,
        text: textContent
      },
      html: htmlContent,
      text: textContent,
      sentAt: new Date(),
      sentBy: userEmail
    };

    await addDoc(collection(this.firestore, 'email_receipts'), emailData);
    await setDoc(doc(this.firestore, 'photobooth_config/global'), {
      lastMachineStatusEmailSentAt: serverTimestamp()
    }, { merge: true });

    return { success: true, count: machinesList.length, fallback: true };
  }

  generateMachineStatusEmailHtml(machines: any[]): string {
    const nonTestingMachines = (machines || []).filter(m => !m.isTesting);
    const total = nonTestingMachines.length;
    const offlineCount = nonTestingMachines.filter(m => m.overallStatus === 'offline').length;
    const warningCount = nonTestingMachines.filter(m => m.overallStatus === 'warning').length;
    const onlineCount = nonTestingMachines.filter(m => m.overallStatus === 'online').length;
    const timestampPT = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      dateStyle: 'full',
      timeStyle: 'medium'
    }).format(new Date()) + ' PT';

    const renderBadge = (status: 'green' | 'red' | 'orange' | 'yellow' | 'gray', label: string) => {
      const colors = {
        green: { bg: '#ecfdf5', color: '#065f46', border: '#a7f3d0' },
        yellow: { bg: '#fefce8', color: '#854d0e', border: '#fef08a' },
        orange: { bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
        red: { bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
        gray: { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' }
      };
      const c = colors[status];
      return `<span style="display:inline-block;padding:3px 8px;font-size:11px;font-weight:700;border-radius:12px;background-color:${c.bg};color:${c.color};border:1px solid ${c.border};text-transform:uppercase;letter-spacing:0.4px;white-space:nowrap;">${label}</span>`;
    };

    let tableRows = '';
    nonTestingMachines.forEach((m, idx) => {
      const rowBg = idx % 2 === 0 ? '#ffffff' : '#f8fafc';

      let machineBadge = '';
      if (m.overallStatus === 'offline') {
        machineBadge = renderBadge('gray', '⚫ Offline');
      } else if (m.overallStatus === 'warning') {
        machineBadge = renderBadge('yellow', m.isInMaintenance ? '🔧 Maintenance' : '⚠️ Warning');
      } else {
        machineBadge = renderBadge('green', '🟢 Online');
      }

      const cameraBadge = m.cameraConnected ? renderBadge('green', 'Connected') : renderBadge('red', 'Disconnected');

      let cardBadge = '';
      if (m.cardConnected === null) {
        cardBadge = renderBadge('gray', 'Not Used');
      } else if (m.cardConnected) {
        cardBadge = renderBadge('green', 'Connected');
      } else {
        cardBadge = renderBadge('red', 'Disconnected');
      }

      let cashBadge = '';
      if (m.cashConnected === null) {
        cashBadge = renderBadge('gray', 'Not Used');
      } else if (m.cashConnected) {
        cashBadge = renderBadge('green', 'Connected');
      } else {
        cashBadge = renderBadge('red', 'Disconnected');
      }

      const hasPapers = m.remainingPapers !== undefined && m.remainingPapers !== null && m.remainingPapers !== '';
      const papersNum = hasPapers ? Number(m.remainingPapers) : NaN;
      const papersDisplay = hasPapers
        ? `<span style="font-weight:700;color:${!isNaN(papersNum) && papersNum < 50 ? '#dc2626' : '#0f172a'};">${m.remainingPapers}</span>`
        : `<span style="color:#94a3b8;font-weight:normal;">N/A</span>`;

      tableRows += `
        <tr style="background-color:${rowBg};border-bottom:1px solid #e2e8f0;">
          <td style="padding:10px 12px;font-size:13px;color:#0f172a;font-weight:600;">
            ${m.deviceName} ${m.isTesting ? '<span style="font-size:10px;padding:2px 5px;background:#fef3c7;color:#92400e;border-radius:3px;margin-left:4px;">TEST</span>' : ''}
            <div style="font-size:11px;font-family:monospace;color:#64748b;font-weight:normal;margin-top:2px;">${m.deviceId}</div>
          </td>
          <td style="padding:10px 12px;font-size:12px;color:#334155;">${m.storeName}</td>
          <td style="padding:10px 12px;text-align:center;">${machineBadge}</td>
          <td style="padding:10px 12px;text-align:center;font-size:12px;">${papersDisplay}</td>
          <td style="padding:10px 12px;text-align:center;">${cameraBadge}</td>
          <td style="padding:10px 12px;text-align:center;">${cardBadge}</td>
          <td style="padding:10px 12px;text-align:center;">${cashBadge}</td>
          <td style="padding:10px 12px;font-size:12px;color:#64748b;white-space:nowrap;">${m.lastSeenStr}</td>
        </tr>
      `;
    });

    return `
      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;background-color:#f1f5f9;padding:24px 16px;margin:0;">
        <div style="max-width:1160px;margin:0 auto;background-color:#ffffff;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.06);overflow:hidden;border:1px solid #e2e8f0;">
          
          <div style="background:linear-gradient(135deg, #1e293b 0%, #0f172a 100%);color:#ffffff;padding:24px 28px;">
            <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px;">
              <div>
                <h1 style="margin:0;font-size:22px;font-weight:700;letter-spacing:-0.3px;">📸 Photobooth Fleet Status Report</h1>
                <p style="margin:6px 0 0 0;font-size:13px;color:#94a3b8;">Automated System Telemetry & Peripheral Diagnostic Summary</p>
              </div>
              <div style="text-align:right;">
                <span style="display:inline-block;padding:4px 10px;background:rgba(255,255,255,0.1);border-radius:6px;font-size:12px;color:#e2e8f0;font-weight:500;">
                  ${timestampPT}
                </span>
              </div>
            </div>
          </div>

          <div style="padding:20px 24px 12px 24px;background-color:#f8fafc;border-bottom:1px solid #e2e8f0;">
            <table style="width:100%;border-collapse:collapse;" role="presentation">
              <tr>
                <td style="width:25%;padding:6px;">
                  <div style="background:#ffffff;border:1px solid #e2e8f0;border-radius:6px;padding:12px 14px;text-align:center;">
                    <div style="font-size:11px;color:#64748b;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Total Fleet</div>
                    <div style="font-size:22px;font-weight:800;color:#0f172a;margin-top:4px;">${total}</div>
                  </div>
                </td>
                <td style="width:25%;padding:6px;">
                  <div style="background:#f1f5f9;border:1px solid #cbd5e1;border-radius:6px;padding:12px 14px;text-align:center;">
                    <div style="font-size:11px;color:#475569;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Offline</div>
                    <div style="font-size:22px;font-weight:800;color:#334155;margin-top:4px;">${offlineCount}</div>
                  </div>
                </td>
                <td style="width:25%;padding:6px;">
                  <div style="background:#fefce8;border:1px solid #fef08a;border-radius:6px;padding:12px 14px;text-align:center;">
                    <div style="font-size:11px;color:#854d0e;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Warning</div>
                    <div style="font-size:22px;font-weight:800;color:#a16207;margin-top:4px;">${warningCount}</div>
                  </div>
                </td>
                <td style="width:25%;padding:6px;">
                  <div style="background:#ecfdf5;border:1px solid #a7f3d0;border-radius:6px;padding:12px 14px;text-align:center;">
                    <div style="font-size:11px;color:#065f46;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Online</div>
                    <div style="font-size:22px;font-weight:800;color:#047857;margin-top:4px;">${onlineCount}</div>
                  </div>
                </td>
              </tr>
            </table>
          </div>

          <div style="padding:20px 24px;overflow-x:auto;">
            <table style="width:100%;border-collapse:collapse;text-align:left;" role="presentation">
              <thead>
                <tr style="background-color:#f1f5f9;border-bottom:2px solid #cbd5e1;">
                  <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;">Machine</th>
                  <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;">Store / Location</th>
                  <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:center;">Status</th>
                  <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:center;">Papers</th>
                  <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:center;">Camera</th>
                  <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:center;">Card Reader</th>
                  <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;text-align:center;">Cash Acceptor</th>
                  <th style="padding:10px 12px;font-size:11px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.5px;">Last Telemetry</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>

          <div style="padding:16px 24px;background-color:#f8fafc;border-top:1px solid #e2e8f0;text-align:center;font-size:12px;color:#94a3b8;">
            <p style="margin:0 0 4px 0;">This is an automated operational report generated by KCEG Photobooth Management.</p>
            <p style="margin:0;">To modify report timing or recipients, visit Photobooth Management &gt; Global Configuration in Web ERP.</p>
          </div>

        </div>
      </div>
    `;
  }

  // --- Storage Methods ---

  async uploadLayoutBackground(isGlobal: boolean, deviceId: string, file: File): Promise<string> {
    const devPath = isGlobal ? 'global' : deviceId;
    const storagePathDir = `Photobooth/${devPath}/Assets/`;
    const dirRef = ref(this.storage, storagePathDir);

    // Find and delete any existing backgrounds
    try {
      const result = await listAll(dirRef);
      const existingBackgrounds = result.items.filter(item => item.name.includes('_TBG'));
      await Promise.all(existingBackgrounds.map(item => this.deleteOwnedObject(isGlobal, deviceId, item.fullPath)));
    } catch (error: any) {
      if (error.code !== 'storage/object-not-found') {
        console.warn('Warning: Could not list files to delete old backgrounds', error);
      }
    }

    const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
    const newFileName = `${fileNameWithoutExt}_TBG.png`;
    const storagePath = `Photobooth/${devPath}/Assets/${newFileName}`;

    const storageRef = ref(this.storage, storagePath);
    await uploadBytes(storageRef, file);
    return storagePath;
  }

  async getLayoutBackground(isGlobal: boolean, deviceId: string, versionId: string | null = null): Promise<{ name: string, url: string } | null> {
    const devPath = isGlobal ? 'global' : deviceId;
    const getStoragePath = (dev: string) => versionId ? `Photobooth/Published/${versionId}/${dev}/Assets/` : `Photobooth/${dev}/Assets/`;

    try {
      let storageRef = ref(this.storage, getStoragePath(devPath));
      let result;
      try {
        result = await listAll(storageRef);
      } catch (error: any) {
        const isStandalone = !isGlobal ? await this.getDeviceOverrideGlobalLayout(deviceId, versionId) : false;
        if (error.code === 'storage/object-not-found' && !isGlobal && !versionId && !isStandalone) {
          storageRef = ref(this.storage, getStoragePath('global'));
          result = await listAll(storageRef);
        } else {
          throw error;
        }
      }

      const tbgItem = result.items.find(item => item.name.includes('_TBG'));
      if (tbgItem) {
        const url = await getDownloadURL(tbgItem);
        return { name: tbgItem.name, url };
      }
      return null;
    } catch (error: any) {
      if (error.code === 'storage/object-not-found') return null;
      throw error;
    }
  }

  async deleteLayoutBackground(isGlobal: boolean, deviceId: string): Promise<void> {
    const devPath = isGlobal ? 'global' : deviceId;
    const storagePathDir = `Photobooth/${devPath}/Assets/`;
    const dirRef = ref(this.storage, storagePathDir);

    try {
      const result = await listAll(dirRef);
      const existingBackgrounds = result.items.filter(item => item.name.includes('_TBG'));
      await Promise.all(existingBackgrounds.map(item => this.deleteOwnedObject(isGlobal, deviceId, item.fullPath)));
    } catch (error: any) {
      if (error.code !== 'storage/object-not-found') {
        throw error;
      }
    }
  }

  async uploadPrintingBackground(isGlobal: boolean, deviceId: string, file: File): Promise<string> {
    const devPath = isGlobal ? 'global' : deviceId;
    const storagePathDir = `Photobooth/${devPath}/Videos/printing/`;
    const dirRef = ref(this.storage, storagePathDir);

    // Find and delete any existing printing backgrounds
    try {
      const result = await listAll(dirRef);
      const referenced = await this.referencedPrintingBackdropPaths(isGlobal, deviceId);
      const existingBackgrounds = result.items.filter(item => item.name.includes('_PBG') && !referenced.has(item.fullPath));
      await Promise.all(existingBackgrounds.map(item => this.deleteOwnedObject(isGlobal, deviceId, item.fullPath)));
    } catch (error: any) {
      if (error.code !== 'storage/object-not-found') {
        console.warn('Warning: Could not list files to delete old printing backgrounds', error);
      }
    }

    const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
    const newFileName = `${fileNameWithoutExt}_PBG.png`;
    const storagePath = `Photobooth/${devPath}/Videos/printing/${newFileName}`;

    const storageRef = ref(this.storage, storagePath);
    await uploadBytes(storageRef, file);
    return storagePath;
  }

  async getPrintingBackground(isGlobal: boolean, deviceId: string, versionId: string | null = null): Promise<{ name: string, url: string } | null> {
    const devPath = isGlobal ? 'global' : deviceId;
    const getStoragePath = (dev: string) => versionId ? `Photobooth/Published/${versionId}/${dev}/Videos/printing/` : `Photobooth/${dev}/Videos/printing/`;

    try {
      let storageRef = ref(this.storage, getStoragePath(devPath));
      let result;
      try {
        result = await listAll(storageRef);
      } catch (error: any) {
        const isStandalone = !isGlobal ? await this.getDeviceOverrideGlobalLayout(deviceId, versionId) : false;
        if (error.code === 'storage/object-not-found' && !isGlobal && !versionId && !isStandalone) {
          storageRef = ref(this.storage, getStoragePath('global'));
          result = await listAll(storageRef);
        } else {
          throw error;
        }
      }

      const pbgItem = result.items.find(item => item.name.includes('_PBG'));
      if (pbgItem) {
        const url = await getDownloadURL(pbgItem);
        return { name: pbgItem.name, url };
      }
      return null;
    } catch (error: any) {
      if (error.code === 'storage/object-not-found') return null;
      throw error;
    }
  }

  /** Storage paths of coupled printing backdrops still referenced by printingVideos[].backgroundUrl (never deleted by the legacy single-backdrop actions). */
  private async referencedPrintingBackdropPaths(isGlobal: boolean, deviceId: string): Promise<Set<string>> {
    const snap = await getDoc(doc(this.firestore, isGlobal ? 'photobooth_config/global' : `photobooth_config/${deviceId}`));
    const out = new Set<string>();
    const videos: any[] = snap.exists() ? snap.data()['printingVideos'] || [] : [];
    videos.forEach(v => {
      const p = typeof v === 'object' && v ? storagePathFromDownloadUrl(v.backgroundUrl) : null;
      if (p) out.add(p);
    });
    return out;
  }

  async deletePrintingBackground(isGlobal: boolean, deviceId: string): Promise<void> {
    const devPath = isGlobal ? 'global' : deviceId;
    const storagePathDir = `Photobooth/${devPath}/Videos/printing/`;
    const dirRef = ref(this.storage, storagePathDir);

    try {
      const result = await listAll(dirRef);
      const referenced = await this.referencedPrintingBackdropPaths(isGlobal, deviceId);
      const existingBackgrounds = result.items.filter(item => item.name.includes('_PBG') && !referenced.has(item.fullPath));
      await Promise.all(existingBackgrounds.map(item => this.deleteOwnedObject(isGlobal, deviceId, item.fullPath)));
    } catch (error: any) {
      if (error.code !== 'storage/object-not-found') {
        throw error;
      }
    }
  }

  async uploadFrameGroupBackground(isGlobal: boolean, deviceId: string, themeId: string, file: File, layer: 'bottom' | 'top'): Promise<string> {
    const devPath = isGlobal ? 'global' : deviceId;
    const storagePathDir = `Photobooth/${devPath}/Assets/${themeId}/`;
    const dirRef = ref(this.storage, storagePathDir);

    try {
      const result = await listAll(dirRef);
      const existingBackgrounds = result.items.filter(item =>
        (layer === 'bottom' && (item.name.includes('_FGBG_BOTTOM') || item.name.endsWith('_FGBG.png'))) ||
        (layer === 'top' && item.name.includes('_FGBG_TOP'))
      );
      await Promise.all(existingBackgrounds.map(item => this.deleteOwnedObject(isGlobal, deviceId, item.fullPath)));
    } catch (error: any) {
      if (error.code !== 'storage/object-not-found') {
        console.warn('Warning: Could not list files to delete old backgrounds', error);
      }
    }

    const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
    const suffix = layer === 'bottom' ? '_FGBG_BOTTOM' : '_FGBG_TOP';
    const newFileName = `${fileNameWithoutExt}${suffix}.png`;
    const storagePath = `Photobooth/${devPath}/Assets/${themeId}/${newFileName}`;

    const storageRef = ref(this.storage, storagePath);
    await uploadBytes(storageRef, file);
    return storagePath;
  }

  async getFrameGroupBackground(isGlobal: boolean, deviceId: string, themeId: string, versionId: string | null = null): Promise<{ bottom: { name: string, url: string } | null, top: { name: string, url: string } | null }> {
    const devPath = isGlobal ? 'global' : deviceId;
    const getStoragePath = (dev: string) => versionId ? `Photobooth/Published/${versionId}/${dev}/Assets/${themeId}/` : `Photobooth/${dev}/Assets/${themeId}/`;
    const response = { bottom: null as any, top: null as any };

    try {
      let storageRef = ref(this.storage, getStoragePath(devPath));
      let result;
      try {
        result = await listAll(storageRef);
      } catch (error: any) {
        const isStandalone = !isGlobal ? await this.getDeviceOverrideGlobalLayout(deviceId, versionId) : false;
        if (error.code === 'storage/object-not-found' && !isGlobal && !versionId && !isStandalone) {
          storageRef = ref(this.storage, getStoragePath('global'));
          result = await listAll(storageRef);
        } else {
          throw error;
        }
      }

      const bottomItem = result.items.find(item => item.name.includes('_FGBG_BOTTOM') || item.name.endsWith('_FGBG.png'));
      const topItem = result.items.find(item => item.name.includes('_FGBG_TOP'));

      if (bottomItem) response.bottom = { name: bottomItem.name, url: await getDownloadURL(bottomItem) };
      if (topItem) response.top = { name: topItem.name, url: await getDownloadURL(topItem) };

      return response;
    } catch (error: any) {
      if (error.code === 'storage/object-not-found') return response;
      throw error;
    }
  }

  async deleteFrameGroupBackground(isGlobal: boolean, deviceId: string, themeId: string, layer: 'bottom' | 'top'): Promise<void> {
    const devPath = isGlobal ? 'global' : deviceId;
    const storagePathDir = `Photobooth/${devPath}/Assets/${themeId}/`;
    const dirRef = ref(this.storage, storagePathDir);

    try {
      const result = await listAll(dirRef);
      const existingBackgrounds = result.items.filter(item =>
        (layer === 'bottom' && (item.name.includes('_FGBG_BOTTOM') || item.name.endsWith('_FGBG.png'))) ||
        (layer === 'top' && item.name.includes('_FGBG_TOP'))
      );
      await Promise.all(existingBackgrounds.map(item => this.deleteOwnedObject(isGlobal, deviceId, item.fullPath)));
    } catch (error: any) {
      if (error.code !== 'storage/object-not-found') {
        throw error;
      }
    }
  }

  async uploadThemeThumbnail(isGlobal: boolean, deviceId: string, themeId: string, file: File): Promise<string> {
    const devPath = isGlobal ? 'global' : deviceId;
    const storagePathDir = `Photobooth/${devPath}/Assets/${themeId}/`;
    const dirRef = ref(this.storage, storagePathDir);

    // Find and delete any existing thumbnails
    try {
      const result = await listAll(dirRef);
      const existingThumbnails = result.items.filter(item => item.name.includes('_TTB'));
      await Promise.all(existingThumbnails.map(item => this.deleteOwnedObject(isGlobal, deviceId, item.fullPath)));
    } catch (error: any) {
      // It's okay if the directory doesn't exist yet (e.g. storage/object-not-found)
      if (error.code !== 'storage/object-not-found') {
        console.warn('Warning: Could not list files to delete old thumbnails', error);
      }
    }

    const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
    const newFileName = `${fileNameWithoutExt}_TTB.png`;
    const storagePath = `Photobooth/${devPath}/Assets/${themeId}/${newFileName}`;

    const storageRef = ref(this.storage, storagePath);
    await uploadBytes(storageRef, file);
    return storagePath;
  }

  async getThemeThumbnail(isGlobal: boolean, deviceId: string, themeId: string, versionId: string | null = null): Promise<{ name: string, url: string } | null> {
    const devPath = isGlobal ? 'global' : deviceId;
    const getStoragePath = (dev: string) => versionId ? `Photobooth/Published/${versionId}/${dev}/Assets/${themeId}/` : `Photobooth/${dev}/Assets/${themeId}/`;

    try {
      let storageRef = ref(this.storage, getStoragePath(devPath));
      let result: any = null;
      try {
        result = await listAll(storageRef);
      } catch (error: any) {
        // Storage directory might not exist yet for this device
        result = null;
      }

      let ttbItem = result?.items?.find((item: any) => item.name.includes('_TTB'));

      // If not found on device, check global folder (unless device has standalone layout override or versionId is specified)
      if (!ttbItem && !isGlobal && !versionId) {
        try {
          const isStandalone = await this.getDeviceOverrideGlobalLayout(deviceId, versionId);
          if (!isStandalone) {
            const globalRef = ref(this.storage, getStoragePath('global'));
            const globalResult = await listAll(globalRef);
            ttbItem = globalResult.items.find((item: any) => item.name.includes('_TTB'));
          }
        } catch (err: any) {
          // Ignore global lookup errors
        }
      }

      if (ttbItem) {
        const url = await getDownloadURL(ttbItem);
        return { name: ttbItem.name, url };
      }
      return null;
    } catch (error: any) {
      if (error.code === 'storage/object-not-found') return null;
      throw error;
    }
  }

  async deleteThemeThumbnail(isGlobal: boolean, deviceId: string, themeId: string): Promise<void> {
    const devPath = isGlobal ? 'global' : deviceId;
    const storagePathDir = `Photobooth/${devPath}/Assets/${themeId}/`;
    const dirRef = ref(this.storage, storagePathDir);

    try {
      const result = await listAll(dirRef);
      const existingThumbnails = result.items.filter(item => item.name.includes('_TTB'));
      await Promise.all(existingThumbnails.map(item => this.deleteOwnedObject(isGlobal, deviceId, item.fullPath)));
    } catch (error: any) {
      if (error.code !== 'storage/object-not-found') {
        throw error;
      }
    }
  }

  async uploadFrameAsset(isGlobal: boolean, deviceId: string, themeId: string, frameId: string, file: File, suffix: string): Promise<string> {
    const devPath = isGlobal ? 'global' : deviceId;
    const groupDocRef = doc(this.firestore, this.getFramesCollectionPath(isGlobal, deviceId), `${themeId}_${frameId}`);
    const groupSnap = await getDoc(groupDocRef).catch(() => null);
    const existingUploadedFrames = groupSnap?.exists() ? (groupSnap.data()['uploadedFrames'] || []) : [];

    const { generatedFileName } = resolveFrameAnchor(file.name, suffix, existingUploadedFrames);
    const storagePath = `Photobooth/${devPath}/Assets/${themeId}/${frameId}/${generatedFileName}`;

    const storageRef = ref(this.storage, storagePath);
    await uploadBytes(storageRef, file);
    return storagePath;
  }

  async uploadPreview(isGlobal: boolean, deviceId: string, themeId: string, frameId: string, fpFile: File): Promise<{ fpName: string }> {
    const devPath = isGlobal ? 'global' : deviceId;
    const groupDocRef = doc(this.firestore, this.getFramesCollectionPath(isGlobal, deviceId), `${themeId}_${frameId}`);
    const groupSnap = await getDoc(groupDocRef).catch(() => null);
    const existingUploadedFrames = groupSnap?.exists() ? (groupSnap.data()['uploadedFrames'] || []) : [];

    const { generatedFileName: fpName } = resolveFrameAnchor(fpFile.name, '_FP', existingUploadedFrames);

    await uploadBytes(ref(this.storage, `Photobooth/${devPath}/Assets/${themeId}/${frameId}/${fpName}`), fpFile);

    return { fpName };
  }

  async getFrameAssets(isGlobal: boolean, deviceId: string, themeId: string, frameId: string, versionId: string | null = null): Promise<any[]> {
    const devPath = isGlobal ? 'global' : deviceId;
    const getStoragePath = (dev: string) => versionId ? `Photobooth/Published/${versionId}/${dev}/Assets/${themeId}/${frameId}/` : `Photobooth/${dev}/Assets/${themeId}/${frameId}/`;

    try {
      let storageRef = ref(this.storage, getStoragePath(devPath));
      let result = await listAll(storageRef).catch(() => ({ items: [], prefixes: [] }));

      // A standalone device never reads or lists the global library (KAN-517 AC-1); inheriting devices keep the fallback.
      if ((!result || result.items.length === 0) && !isGlobal && !(await this.isStandaloneByFlags(deviceId))) {
        storageRef = ref(this.storage, getStoragePath('global'));
        result = await listAll(storageRef).catch(() => ({ items: [], prefixes: [] }));
      }

      const assets = await Promise.all(result.items.map(async (itemRef) => {
        const url = await getDownloadURL(itemRef);
        const metadata = await getMetadata(itemRef);
        return {
          name: itemRef.name,
          fullPath: itemRef.fullPath,
          url: url,
          timeCreated: metadata.timeCreated
        };
      }));
      return assets;
    } catch (error: any) {
      if (error.code === 'storage/object-not-found') return [];
      throw error;
    }
  }

  /** Deletes a listed frame asset only when this scope owns it; a global/foreign path is kept ('reference-only', KAN-517). */
  async deleteFrameAsset(fullPath: string, isGlobal: boolean, deviceId: string): Promise<StorageDeleteOutcome> {
    return this.deleteOwnedObject(isGlobal, deviceId, fullPath);
  }

  async syncDraftAssetsToPublished(): Promise<void> {
    const draftBase = 'Photobooth';
    const publishedBase = 'Photobooth/Published';

    // 1. Clear the published directory first so we don't leave deleted files behind
    await this.deleteStorageDirectory(publishedBase);

    // 2. Copy the entire draft directory
    await this.copyStorageDirectory(draftBase, publishedBase);
  }

  async deleteHistoricalVersion(versionId: string): Promise<void> {
    const versionDocPath = `photobooth_config_published/${versionId}`;

    // 1. Delete Firestore Subcollections (config/global/frame_groups, config/deviceId/frame_groups, etc.)
    // Since recursive delete from client SDK is hard, we can call an admin function or just delete the known ones:
    const devicesSnap = await getDocs(collection(this.firestore, 'devices'));
    const deviceIds = ['global', ...devicesSnap.docs.map(d => d.id).filter(id => id !== 'global')];

    for (const deviceId of deviceIds) {
      const framesSnap = await getDocs(collection(this.firestore, `${versionDocPath}/config/${deviceId}/frame_groups`));
      await Promise.all(framesSnap.docs.map(d => deleteDoc(d.ref)));
      await deleteDoc(doc(this.firestore, `${versionDocPath}/config/${deviceId}`));
    }

    // 2. Delete main version document
    await deleteDoc(doc(this.firestore, versionDocPath));

    // 3. Delete Storage directory
    const storagePath = `Photobooth/Published/${versionId}`;
    await this.deleteStorageDirectory(storagePath);
  }

  private async deleteStorageDirectory(path: string): Promise<void> {
    const dirRef = ref(this.storage, path);
    try {
      const result = await listAll(dirRef);
      await Promise.all(result.items.map(item => deleteObject(item)));
      await Promise.all(result.prefixes.map(prefix => this.deleteStorageDirectory(prefix.fullPath)));
    } catch (e: any) {
      if (e.code !== 'storage/object-not-found') {
        console.warn('Error deleting storage dir:', e);
      }
    }
  }

  private async listStorageFilesRecursively(path: string): Promise<string[]> {
    const dirRef = ref(this.storage, path);
    try {
      const result = await listAll(dirRef);
      const currentFiles = result.items.map(item => item.fullPath);
      const subResults = await Promise.all(
        result.prefixes.map(prefix => this.listStorageFilesRecursively(prefix.fullPath))
      );
      return [...currentFiles, ...subResults.flat()];
    } catch (e: any) {
      if (e.code !== 'storage/object-not-found') {
        console.warn('Error listing storage dir:', e);
      }
      return [];
    }
  }

  private async copyStorageDirectory(sourcePath: string, destPath: string): Promise<void> {
    const sourceRef = ref(this.storage, sourcePath);
    try {
      const result = await listAll(sourceRef);

      // Copy files
      await Promise.all(result.items.map(async (item) => {
        const destRef = ref(this.storage, `${destPath}/${item.name}`);
        const url = await getDownloadURL(item);
        const response = await fetch(url);
        const blob = await response.blob();
        await uploadBytes(destRef, blob);
      }));

      // Recurse into subfolders
      await Promise.all(result.prefixes.map(prefix => {
        const newDest = `${destPath}/${prefix.name}`;
        return this.copyStorageDirectory(prefix.fullPath, newDest);
      }));
    } catch (e: any) {
      if (e.code !== 'storage/object-not-found') {
        console.warn('Error copying storage dir:', e);
      }
    }
  }

  // --- Frame Methods ---
  async resolveFrameImageUrl(deviceId: string, themeName: string, frameGroupId: string, frameId: string): Promise<{ url: string | null, debugPath: string }> {
    let debugPath = `Started resolution for themeName: ${themeName}, frameGroupId: ${frameGroupId}. `;
    if (!themeName || !frameGroupId) return { url: null, debugPath: 'Missing inputs' };
    let actualThemeId = themeName;
    let isSignature = false;

    // Resolve theme name to actual theme UUID
    try {
      const layout = await this.getDeviceLayout(deviceId, 'current-published');
      const findTile = (tiles: any[]): any => {
        for (const tile of tiles) {
          if (tile.properties?.themeName === themeName ||
            tile.properties?.title === themeName ||
            tile.type === themeName ||
            tile.properties?.tileType === themeName) return tile;
          if (tile.type === 'folder' && tile.tiles) {
            const found = findTile(tile.tiles);
            if (found) return found;
          }
        }
        return null;
      };
      const matched = findTile(layout);
      if (matched && matched.id) {
        actualThemeId = matched.id;
        debugPath += `Matched layout UUID: ${actualThemeId}. Type: ${matched.type}, TileType: ${matched.properties?.tileType}. `;
        if (matched.type === 'Signature' || matched.properties?.tileType === 'Signature') {
          isSignature = true;
        }
      } else {
        debugPath += `Did not match layout. `;
      }
    } catch (e) {
      console.warn('Could not load layout to resolve theme ID', e);
    }

    let targetFrameGroupId = frameGroupId;

    if (isSignature) {
      // Signature themes put all frames in one folder, which might not match the frameGroupId from the session.
      // So we scan the storage folder and pick the first subfolder.
      try {
        let activeVersion = null;
        const pubDocSnap = await getDoc(doc(this.firestore, 'photobooth_config_published/current-published'));
        if (pubDocSnap.exists()) {
          activeVersion = pubDocSnap.data()['activeVersion'];
        }

        let result = null;
        if (activeVersion) {
          let dirRef = ref(this.storage, `Photobooth/Published/${activeVersion}/${deviceId}/Assets/${actualThemeId}/`);
          result = await listAll(dirRef).catch(() => null);
          if (!result || result.prefixes.length === 0) {
            dirRef = ref(this.storage, `Photobooth/Published/${activeVersion}/global/Assets/${actualThemeId}/`);
            result = await listAll(dirRef).catch(() => null);
          }
        }

        if (!result || result.prefixes.length === 0) {
          let dirRef = ref(this.storage, `Photobooth/${deviceId}/Assets/${actualThemeId}/`);
          result = await listAll(dirRef).catch(() => null);
          if (!result || result.prefixes.length === 0) {
            dirRef = ref(this.storage, `Photobooth/global/Assets/${actualThemeId}/`);
            result = await listAll(dirRef).catch(() => null);
          }
        }

        if (result && result.prefixes.length > 0) {
          targetFrameGroupId = result.prefixes[0].name;
          debugPath += `Found Signature folder: ${targetFrameGroupId}. `;
        } else {
          debugPath += `No signature subfolder found. `;
        }
      } catch (e) {
        debugPath += `Error listing signature folders. `;
        console.warn('Could not list Signature theme subfolders', e);
      }
    } else {
      debugPath += `Target frameGroupId remains: ${targetFrameGroupId}. `;
    }

    // 1. Try to get the exact published URL from Firestore config
    const exactUrl = await this.getPublishedFrameImageUrl(deviceId, actualThemeId, targetFrameGroupId, frameId);
    if (exactUrl) {
      debugPath += `Found exactUrl in Firestore config. `;
      return { url: exactUrl, debugPath };
    }

    debugPath += `Failed Firestore config exact URL fetch. `;

    // 2. Fallback to list all files in the device/global storage and take the first matching one
    try {
      debugPath += `Trying fallback getFrameAssets(false, ${deviceId}, ${actualThemeId}, ${targetFrameGroupId}). `;
      let assets = await this.getFrameAssets(false, deviceId, actualThemeId, targetFrameGroupId);
      let fpAsset = assets.find((a: any) => a.name.includes(frameId));
      if (!fpAsset) {
        fpAsset = assets.find((a: any) => a.name.includes('_FP') || a.name.includes('_FTB'));
      }

      if (!fpAsset) {
        debugPath += `Asset not in ${targetFrameGroupId}. Scanning all folders. `;
        const folders = new Set<string>();
        try {
          const pubDocSnap = await getDoc(doc(this.firestore, 'photobooth_config_published/current-published'));
          const activeVersion = pubDocSnap.exists() ? pubDocSnap.data()['activeVersion'] : null;
          const paths = [];
          if (activeVersion) {
            paths.push(`Photobooth/Published/${activeVersion}/${deviceId}/Assets/${actualThemeId}/`);
            paths.push(`Photobooth/Published/${activeVersion}/global/Assets/${actualThemeId}/`);
          }
          paths.push(`Photobooth/${deviceId}/Assets/${actualThemeId}/`);
          paths.push(`Photobooth/global/Assets/${actualThemeId}/`);

          for (const p of paths) {
            const result = await listAll(ref(this.storage, p)).catch(() => null);
            if (result) {
              result.prefixes.forEach(pref => folders.add(pref.name));
            }
          }
        } catch (e) { }

        for (const folder of Array.from(folders)) {
          if (folder === targetFrameGroupId) continue;
          assets = await this.getFrameAssets(false, deviceId, actualThemeId, folder);
          fpAsset = assets.find((a: any) => a.name.includes(frameId));
          if (fpAsset) {
            debugPath += `Found in ${folder}. `;
            break;
          }
        }
      }
      if (!fpAsset && assets.length > 0) {
        fpAsset = assets[0];
      }

      if (fpAsset) {
        debugPath += `Found fallback asset: ${fpAsset.name}. `;
      } else {
        debugPath += `Fallback returned no assets. `;
      }
      return { url: fpAsset ? fpAsset.url : null, debugPath };
    } catch (e) {
      debugPath += `Fallback failed with error. `;
      console.error('Failed to resolve frame image fallback', e);
      return { url: null, debugPath };
    }
  }
  async getPublishedFrameImageUrl(deviceId: string, themeId: string, groupId: string, frameId: string): Promise<string | null> {
    const docId = `${themeId}_${groupId}`;

    // Check device first
    const deviceDocRef = doc(this.firestore, `photobooth_config_published/current-published/config/${deviceId}/frame_groups/${docId}`);
    const deviceDocSnap = await getDoc(deviceDocRef);

    let matchedFrame = null;
    let usedDevice = deviceId;

    if (deviceDocSnap.exists()) {
      const data = deviceDocSnap.data();
      const uploadedFrames: any[] = data['uploadedFrames'] || [];
      matchedFrame = uploadedFrames.find(f => f.frameId === frameId);
    }

    // If frame wasn't found in device (or device doc didn't exist), fallback to global
    if (!matchedFrame) {
      const globalDocRef = doc(this.firestore, `photobooth_config_published/current-published/config/global/frame_groups/${docId}`);
      const globalDocSnap = await getDoc(globalDocRef);

      if (globalDocSnap.exists()) {
        const data = globalDocSnap.data();
        const uploadedFrames: any[] = data['uploadedFrames'] || [];
        matchedFrame = uploadedFrames.find(f => f.frameId === frameId);

        // Fallback to first frame if exact match not found even in global
        if (!matchedFrame && uploadedFrames.length > 0) {
          matchedFrame = uploadedFrames[0];
        }
      }
      usedDevice = 'global';
    }

    if (matchedFrame) {
      // Prefer FP, then PP, then FPBG, then FTB
      const fileName = matchedFrame.fpName || matchedFrame.ppName || matchedFrame.fpbgName || matchedFrame.ftbName;
      if (fileName) {

        // 1. Determine if this tile is customized for this device
        let isCustomized = false;
        try {
          const configRef = doc(this.firestore, `photobooth_config_published/current-published/config/${deviceId}`);
          const configSnap = await getDoc(configRef);
          if (configSnap.exists()) {
            const tiles = configSnap.data()['layout']?.['tiles'] || configSnap.data()['tiles'] || [];

            const findTileById = (arr: any[]): any => {
              for (const t of arr) {
                if (t.id === themeId) return t;
                if (t.type === 'folder' && t.tiles) {
                  const found = findTileById(t.tiles);
                  if (found) return found;
                }
              }
              return null;
            };

            const tile = findTileById(tiles);
            if (tile && (tile.properties?.isCustomizable || tile.properties?.isCustomized)) {
              isCustomized = true;
            }
          }
        } catch (e) {
          console.warn('Error checking if tile is customized', e);
        }

        // 2. Determine version from device collection
        let deviceVersion = 'current-published';

        try {
          const deviceRef = doc(this.firestore, `devices/${deviceId}`);
          const deviceSnap = await getDoc(deviceRef);
          if (deviceSnap.exists() && deviceSnap.data()['publishedVersion']) {
            deviceVersion = `v${deviceSnap.data()['publishedVersion']}`;
          }
        } catch (e) {
          console.warn('Error determining published version', e);
        }

        // 3. Try to get from device storage if customized
        if (isCustomized) {
          const deviceStoragePath = `Photobooth/Published/${deviceVersion}/${deviceId}/Assets/${themeId}/${groupId}/${fileName}`;
          try {
            return await getDownloadURL(ref(this.storage, deviceStoragePath));
          } catch (e) {
            console.warn('Could not find customized frame asset in device storage, falling back to global');
          }
        }

        // 4. Fallback or direct fetch from global storage using the device's published version
        const globalStoragePath = `Photobooth/Published/${deviceVersion}/global/Assets/${themeId}/${groupId}/${fileName}`;
        try {
          return await getDownloadURL(ref(this.storage, globalStoragePath));
        } catch (e) {
          console.warn('Could not find exact published frame asset in global storage', e);
        }
      }
    }

    // If we failed to get it from published config, fallback to old method
    return null;
  }

  private getFramesCollectionPath(isGlobal: boolean, deviceId: string, version?: string | null): string {
    if (version) {
      const basePath = isGlobal ? 'global' : `${deviceId}`;
      return `photobooth_config_published/${version}/config/${basePath}/frame_groups`;
    } else {
      const basePath = isGlobal ? 'global' : `${deviceId}`;
      return `photobooth_config/${basePath}/frame_groups`;
    }
  }

  async getFrames(isGlobal: boolean, deviceId: string, themeId: string, version?: string | null, knownThemeIds?: string[]): Promise<ThemeFrame[]> {
    const path = this.getFramesCollectionPath(isGlobal, deviceId, version);
    const framesRef = collection(this.firestore, path);
    const snapshot = await getDocs(framesRef);

    // KAN-523 AC-10: resolve against all layout theme IDs using longest-prefix matching so `a` and `a_b` never mix groups.
    let allKnownThemes = Array.isArray(knownThemeIds) ? [...knownThemeIds] : [];
    if (allKnownThemes.length === 0) {
      try {
        const layout = isGlobal
          ? await this.getGlobalLayout(version)
          : await this.getDeviceLayout(deviceId, version);
        allKnownThemes = (layout || []).map((t: any) => t?.id).filter(Boolean);
      } catch {
        allKnownThemes = [themeId];
      }
    }

    const matched = filterFrameGroupDocsForTheme(snapshot.docs, themeId, allKnownThemes);
    const frames = matched.map(({ doc: d, frameId }) => ({
      id: frameId,
      name: d.data()['name'] || '',
      description: d.data()['description'] || '',
      uploadedFrames: d.data()['uploadedFrames'] || [],
      orderIndex: d.data()['orderIndex'] || 0
    }));

    return frames.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
  }

  async getAllFrameGroups(isGlobal: boolean, deviceId: string, version?: string | null, knownThemeIds: string[] = []): Promise<{ themeId: string, frameId: string, name: string }[]> {
    const path = this.getFramesCollectionPath(isGlobal, deviceId, version);
    const framesRef = collection(this.firestore, path);
    const snapshot = await getDocs(framesRef);

    // Sort by length descending so that longer matching themes are preferred (e.g. 'theme_123' over 'theme')
    const sortedThemes = [...knownThemeIds].sort((a, b) => b.length - a.length);

    return snapshot.docs.map(doc => {
      let themeId = '';
      let frameId = '';

      const matchedTheme = sortedThemes.find(t => doc.id.startsWith(t + '_'));
      if (matchedTheme) {
        themeId = matchedTheme;
        frameId = doc.id.substring(matchedTheme.length + 1);
      } else {
        // Fallback for orphans: try to guess by first underscore
        const underscoreIndex = doc.id.indexOf('_');
        if (underscoreIndex !== -1) {
          themeId = doc.id.substring(0, underscoreIndex);
          frameId = doc.id.substring(underscoreIndex + 1);
        } else {
          themeId = doc.id;
          frameId = '';
        }
      }

      return {
        themeId,
        frameId,
        name: doc.data()['name'] || ''
      };
    });
  }

  async copyGlobalFrameGroupToDevice(deviceId: string, globalThemeId: string, globalFrameId: string, targetThemeId: string): Promise<void> {
    const copyFn = httpsCallable(this.functions, 'copyGlobalFrameGroup');
    await copyFn({ deviceId, globalThemeId, globalFrameId, targetThemeId });
  }

  async saveFrame(isGlobal: boolean, deviceId: string, themeId: string, frameId: string, frameData: ThemeFrame, oldId: string | null = null): Promise<void> {
    const path = this.getFramesCollectionPath(isGlobal, deviceId);

    if (oldId && oldId !== frameId) {
      await this.deleteFrame(isGlobal, deviceId, themeId, oldId);
    }

    const newDocId = themeId + '_' + frameId;
    await setDoc(doc(this.firestore, path, newDocId), {
      name: frameData.name,
      description: frameData.description,
      uploadedFrames: frameData.uploadedFrames || [],
      orderIndex: frameData.orderIndex || 0,
      updatedAt: new Date()
    }, { merge: true }); // Use merge so we don't overwrite uploadedFrames if already there and omitted
  }

  async updateFrameGroupsOrder(isGlobal: boolean, deviceId: string, themeId: string, frames: { id: string, orderIndex: number }[]): Promise<void> {
    const path = this.getFramesCollectionPath(isGlobal, deviceId);
    const batch = writeBatch(this.firestore);

    frames.forEach(frame => {
      const docRef = doc(this.firestore, path, themeId + '_' + frame.id);
      batch.set(docRef, { orderIndex: frame.orderIndex, updatedAt: new Date() }, { merge: true });
    });

    await batch.commit();
  }

  async reorderUploadedFrames(isGlobal: boolean, deviceId: string, themeId: string, groupId: string, newFpNamesOrder: string[]): Promise<void> {
    const path = this.getFramesCollectionPath(isGlobal, deviceId);
    const docId = themeId + '_' + groupId;
    const docRef = doc(this.firestore, path, docId);

    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      const uploadedFrames: any[] = data['uploadedFrames'] || [];

      uploadedFrames.sort((a, b) => {
        const nameA = typeof a === 'object' ? (a.fpName || a.name || '') : a;
        const nameB = typeof b === 'object' ? (b.fpName || b.name || '') : b;

        const indexA = newFpNamesOrder.findIndex(n => n.toUpperCase() === nameA.toUpperCase());
        const indexB = newFpNamesOrder.findIndex(n => n.toUpperCase() === nameB.toUpperCase());

        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });

      await setDoc(docRef, { uploadedFrames }, { merge: true });
    }
  }

  async deleteFrame(isGlobal: boolean, deviceId: string, themeId: string, frameId: string): Promise<void> {
    const path = this.getFramesCollectionPath(isGlobal, deviceId);
    await deleteDoc(doc(this.firestore, path, themeId + '_' + frameId));
  }

  async deleteFrameGroupAndAssets(isGlobal: boolean, deviceId: string, themeId: string, frameId: string): Promise<void> {
    const basePath = isGlobal ? 'global' : deviceId;

    // 1. Delete assets directory (only inside this scope's own folder, KAN-517 AC-3)
    const dirPath = `Photobooth/${basePath}/Assets/${themeId}/${frameId}`;
    if (isOwnedStoragePath(`${dirPath}/x`, isGlobal, deviceId)) {
      await this.deleteStorageDirectory(dirPath);
    }

    // 2. Delete Firestore document
    await this.deleteFrame(isGlobal, deviceId, themeId, frameId);

    // 3. Check if there are any other frame groups for this theme (using all layout theme IDs for prefix disambiguation)
    let knownThemes = [themeId];
    try {
      const layout = isGlobal ? await this.getGlobalLayout() : await this.getDeviceLayout(deviceId);
      knownThemes = Array.from(new Set([themeId, ...(layout || []).map((t: any) => t?.id).filter(Boolean)]));
    } catch { }
    const allGroups = await this.getAllFrameGroups(isGlobal, deviceId, null, knownThemes);
    const themeGroups = allGroups.filter(g => g.themeId === themeId);

    if (themeGroups.length === 0) {
      // 4. Delete theme thumbnail from storage
      const thumbnailPath = `Photobooth/${basePath}/Assets/${themeId}/theme_thumbnail_TTB.png`;
      try {
        await this.deleteOwnedObject(isGlobal, deviceId, thumbnailPath);
      } catch (e: any) {
        if (e.code !== 'storage/object-not-found') console.warn('Thumbnail not found for deletion', e);
      }
    }
  }

  async addUploadedFrameAssetToGroup(isGlobal: boolean, deviceId: string, themeId: string, groupId: string, frameId: string, assetNames: { fpName?: string, ppName?: string, suffix?: string, name?: string }, frameType: string = 'classic', dateColor: string = '#696969'): Promise<void> {
    const path = this.getFramesCollectionPath(isGlobal, deviceId);
    const docId = themeId + '_' + groupId;
    const docRef = doc(this.firestore, path, docId);

    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      // KAN-523 AC-9: remove any orphan file uploaded for the missing group and surface the confirmed PO error message.
      const devPath = isGlobal ? 'global' : deviceId;
      const candidateFiles = [assetNames.name, assetNames.fpName, assetNames.ppName].filter(Boolean) as string[];
      for (const fileName of candidateFiles) {
        const orphanPath = `Photobooth/${devPath}/Assets/${themeId}/${groupId}/${fileName}`;
        try {
          await this.deleteOwnedObject(isGlobal, deviceId, orphanPath);
        } catch (cleanupErr) {
          console.warn('Could not remove orphan uploaded file for missing group:', orphanPath, cleanupErr);
        }
      }
      throw new Error(STANDALONE_HOUSEKEEPING_MESSAGES.missingFrameGroupOnUpload);
    }

    const data = docSnap.data();
    const uploadedFrames: any[] = data['uploadedFrames'] || [];

    const existingIndex = uploadedFrames.findIndex(f => typeof f === 'object' && f.frameId === frameId);
    const newObj = existingIndex === -1 ? { frameId, CutStyle: frameType, dateColor } : { ...uploadedFrames[existingIndex] };

    const isFpOrSettingsUpdate = !!assetNames.fpName || assetNames.suffix === '_FP' || (!assetNames.suffix && !assetNames.name && !assetNames.ppName);
    if (existingIndex !== -1 && isFpOrSettingsUpdate) {
      newObj.CutStyle = frameType;
      if (dateColor) {
        newObj.dateColor = dateColor;
      }
      delete newObj.type;
    }

    if (assetNames.fpName) newObj.fpName = assetNames.fpName;
    if (assetNames.ppName) newObj.ppName = assetNames.ppName;
    if (assetNames.suffix === '_FTB') newObj.ftbName = assetNames.name;
    if (assetNames.suffix === '_FPBG') newObj.fpbgName = assetNames.name;
    if (assetNames.suffix === '_FP') newObj.fpName = assetNames.name;
    if (assetNames.suffix === '_PP') newObj.ppName = assetNames.name;

    if (existingIndex === -1) {
      uploadedFrames.push(newObj);
    } else {
      uploadedFrames[existingIndex] = newObj;
    }

    await setDoc(docRef, { uploadedFrames }, { merge: true });
  }

  async removeUploadedFrameAssetFromGroup(isGlobal: boolean, deviceId: string, themeId: string, groupId: string, filename: string): Promise<void> {
    const path = this.getFramesCollectionPath(isGlobal, deviceId);
    const docId = themeId + '_' + groupId;
    const docRef = doc(this.firestore, path, docId);

    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      const uploadedFrames: any[] = data['uploadedFrames'] || [];

      let modified = false;
      for (let i = uploadedFrames.length - 1; i >= 0; i--) {
        const frameObj = uploadedFrames[i];
        if (typeof frameObj === 'object') {
          if (frameObj.fpName === filename) { delete frameObj.fpName; modified = true; }
          if (frameObj.ppName === filename) { delete frameObj.ppName; modified = true; }
          if (frameObj.ftbName === filename) { delete frameObj.ftbName; modified = true; }
          if (frameObj.fpbgName === filename) { delete frameObj.fpbgName; modified = true; }

          if (!frameObj.fpName && !frameObj.ppName && !frameObj.ftbName && !frameObj.fpbgName) {
            uploadedFrames.splice(i, 1);
            modified = true;
          }
        } else if (frameObj === filename) {
          uploadedFrames.splice(i, 1);
          modified = true;
        }
      }

      if (modified) {
        await setDoc(docRef, { uploadedFrames }, { merge: true });
      }
    }
  }

  // --- Publish Methods ---

  async getPublishedVersions(): Promise<any[]> {
    const publishedCol = collection(this.firestore, 'photobooth_config_published');
    const q = query(publishedCol, orderBy('version', 'desc'));
    const snap = await getDocs(q);
    return snap.docs
      .filter(doc => doc.id !== 'current-published' && doc.id !== 'active_version' && doc.id !== 'app_release')
      .map(doc => {
        const data = doc.data();
        let publishedAt = data['publishedAt'];
        if (publishedAt && typeof publishedAt.toDate === 'function') {
          publishedAt = publishedAt.toDate();
        } else if (publishedAt && typeof publishedAt === 'object' && 'seconds' in publishedAt) {
          publishedAt = new Date(publishedAt.seconds * 1000 + (publishedAt.nanoseconds || 0) / 1000000);
        }
        return {
          id: doc.id,
          ...data,
          publishedAt: publishedAt
        };
      });
  }

  async getActivePublishedVersion(): Promise<string | null> {
    const docSnap = await getDoc(doc(this.firestore, 'photobooth_config_published/active_version'));
    if (docSnap.exists()) {
      return docSnap.data()['activeVersion'];
    }
    return null;
  }

  async createVersionEntry(versionId: string, nextVersion: number): Promise<void> {
    const docRef = doc(this.firestore, 'photobooth_config_published', versionId);

    await setDoc(docRef, {
      version: nextVersion,
      status: 'publishing',
      initiatedAt: new Date(),
    });
  }

  /** Resolves with the completed request document (KAN-515: standalone publishes carry `result.message`); rejects with the function's `error` text. */
  async publishDeviceConfig(deviceId: string): Promise<any> {
    const publishDocRef = doc(collection(this.firestore, 'device_publish_requests'));
    await setDoc(publishDocRef, {
      deviceId: deviceId,
      status: 'pending',
      createdAt: new Date(),
      publishedBy: this.auth.currentUser?.email || 'Unknown'
    });

    return new Promise<any>((resolve, reject) => {
      const unsubscribe = onSnapshot(publishDocRef, (docSnap) => {
        const data = docSnap.data();
        if (data?.['status'] === 'completed') {
          unsubscribe();
          resolve(data);
        } else if (data?.['status'] === 'error') {
          unsubscribe();
          reject(new Error(data?.['error'] || 'Publish failed'));
        }
      });
    });
  }

  listenToLatestPublishStatus(callback: (doc: any) => void): () => void {
    const publishedCol = collection(this.firestore, 'photobooth_config_published');
    const q = query(publishedCol, orderBy('version', 'desc'), limit(10));
    return onSnapshot(q, (snapshot) => {
      const latestDoc = snapshot.docs.find(d =>
        d.id !== 'current-published' &&
        d.id !== 'active_version' &&
        d.id !== 'app_release' &&
        d.id !== 'legal'
      );
      if (latestDoc) {
        callback(latestDoc.data());
      }
    });
  }

  listenToVersionPublishStatus(versionId: string, callback: (doc: any) => void): () => void {
    const docRef = doc(this.firestore, `photobooth_config_published/${versionId}`);
    return onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        callback(docSnap.data());
      }
    });
  }

  async publishConfig(): Promise<number> {
    // 1. Get latest version
    const publishedCol = collection(this.firestore, 'photobooth_config_published');
    const q = query(publishedCol);
    const snap = await getDocs(q);

    // Find the highest integer version number, ignoring strings and special docs
    let nextVersion = 1;
    let maxVersion = 0;

    snap.docs.forEach(d => {
      if (d.id !== 'current-published' && d.id !== 'active_version' && d.id !== 'app_release') {
        const v = d.data()['version'];
        if (typeof v === 'number' && v > maxVersion) {
          maxVersion = v;
        }
      }
    });

    nextVersion = maxVersion + 1;

    const versionDocPath = `photobooth_config_published/v${nextVersion}`;

    // Delete any existing folders in Firebase Storage for the version that we are publishing before publishing
    const storagePath = `Photobooth/Published/v${nextVersion}`;
    await this.deleteStorageDirectory(storagePath);

    // 2. Save Version Metadata with status: publishing
    // This will trigger the Cloud Function to process the copy and update status to 'completed'
    const metadata = {
      version: nextVersion,
      status: 'publishing',
      publishedAt: new Date().toISOString(),
      publishedBy: this.auth.currentUser?.email || 'Unknown'
    };
    await setDoc(doc(this.firestore, versionDocPath), metadata);

    return nextVersion;
  }

  async deleteMachine(deviceId: string): Promise<void> {
    const deletePhotoboothMachineFn = httpsCallable(this.functions, 'deletePhotoboothMachine');
    await deletePhotoboothMachineFn({ deviceId });
  }

  // --- Remote Control Methods ---

  async sendRemoteCommand(deviceId: string, command: string, payload: any = {}): Promise<void> {
    const commandDocRef = doc(this.firestore, `devices/${deviceId}/commands/latest`);
    const timestamp = new Date().toISOString();

    await setDoc(commandDocRef, {
      command,
      payload,
      status: 'pending',
      timestamp: timestamp
    });

    // Also write structured audit log to devices/{deviceId}/logs subcollection
    try {
      const logsColRef = collection(this.firestore, `devices/${deviceId}/logs`);
      await addDoc(logsColRef, {
        timestamp: new Date(),
        type: command.includes('MAINTENANCE') ? command : 'REMOTE_COMMAND',
        command: command,
        cause: payload?.reason || `Remote command executed: ${command}`,
        source: payload?.source || 'ERP_SYSTEM',
        deviceId: deviceId,
        payload: payload
      });
    } catch (logErr) {
      console.warn(`[PhotoboothService] Failed to write log for command ${command}:`, logErr);
    }
  }

  listenToRemoteCommandStatus(deviceId: string, callback: (data: any) => void): () => void {
    const commandDocRef = doc(this.firestore, `devices/${deviceId}/commands/latest`);
    return onSnapshot(commandDocRef, (docSnap) => {
      if (docSnap.exists()) {
        callback(docSnap.data());
      }
    });
  }

  async getDailyRevenue(deviceId: string, period: string, date: string): Promise<any | null> {
    const docRef = doc(this.firestore, `devices/${deviceId}/reports/${period}/daily/${date}`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  }

  async overrideDailyAndMonthlyRevenue(
    deviceId: string,
    period: string,
    dateStr: string,
    newCardTotal: number,
    newCashTotal: number,
    otherDailyData?: {
      CouponTotal?: number;
      DailyOverpaidAmount?: number;
      DailySessions?: number;
      DailyAnimefiedGenerated?: number;
      DailyAnimefiedUsed?: number;
    },
    overrideReason?: string,
    updatedBy?: string
  ): Promise<void> {
    const dailyDocRef = doc(this.firestore, `devices/${deviceId}/reports/${period}/daily/${dateStr}`);
    const monthlyDocRef = doc(this.firestore, `devices/${deviceId}/reports/${period}`);

    const dailySnap = await getDoc(dailyDocRef);
    const existingDaily = dailySnap.exists() ? dailySnap.data() : {};

    // Keep first original values if not already preserved
    const originalValues = existingDaily['originalValues'] || {
      CardTotal: Number(existingDaily['CardTotal'] || existingDaily['cardTotal'] || existingDaily['credit'] || 0),
      CashTotal: Number(existingDaily['CashTotal'] || existingDaily['cashTotal'] || existingDaily['cash'] || 0),
      DailyRevenue: Number(existingDaily['DailyRevenue'] || 0),
      DailySessions: Number(existingDaily['DailySessions'] || existingDaily['dailySessions'] || existingDaily['totalSession'] || 0),
      CouponTotal: Number(existingDaily['CouponTotal'] || existingDaily['couponTotal'] || existingDaily['coupon'] || 0),
      DailyOverpaidAmount: Number(existingDaily['DailyOverpaidAmount'] || existingDaily['overpaidAmount'] || existingDaily['overCharge'] || 0)
    };

    const now = new Date();
    const historyEntry = {
      previousCard: Number(existingDaily['CardTotal'] || existingDaily['cardTotal'] || existingDaily['credit'] || 0),
      previousCash: Number(existingDaily['CashTotal'] || existingDaily['cashTotal'] || existingDaily['cash'] || 0),
      newCard: Number(newCardTotal) || 0,
      newCash: Number(newCashTotal) || 0,
      overrideReason: overrideReason || '',
      updatedBy: updatedBy || 'Unknown User',
      updatedAt: now
    };

    const existingHistory = Array.isArray(existingDaily['overrideHistory']) ? existingDaily['overrideHistory'] : [];

    const updatedDaily = {
      ...existingDaily,
      CardTotal: Number(newCardTotal) || 0,
      CashTotal: Number(newCashTotal) || 0,
      DailyRevenue: (Number(newCardTotal) || 0) + (Number(newCashTotal) || 0),
      ...(otherDailyData?.CouponTotal !== undefined ? { CouponTotal: Number(otherDailyData.CouponTotal) || 0 } : {}),
      ...(otherDailyData?.DailyOverpaidAmount !== undefined ? { DailyOverpaidAmount: Number(otherDailyData.DailyOverpaidAmount) || 0 } : {}),
      ...(otherDailyData?.DailySessions !== undefined ? { DailySessions: Number(otherDailyData.DailySessions) || 0 } : {}),
      ...(otherDailyData?.DailyAnimefiedGenerated !== undefined ? { DailyAnimefiedGenerated: Number(otherDailyData.DailyAnimefiedGenerated) || 0 } : {}),
      ...(otherDailyData?.DailyAnimefiedUsed !== undefined ? { DailyAnimefiedUsed: Number(otherDailyData.DailyAnimefiedUsed) || 0 } : {}),
      originalValues: originalValues,
      overrideReason: overrideReason || '',
      updatedBy: updatedBy || 'Unknown User',
      updatedAt: now,
      isManualOverride: true,
      overrideHistory: [...existingHistory, historyEntry]
    };

    // Save daily document
    await setDoc(dailyDocRef, updatedDaily, { merge: true });

    // Recalculate monthly totals by aggregating all daily docs in the period
    const dailyCol = collection(this.firestore, `devices/${deviceId}/reports/${period}/daily`);
    const allDailiesSnap = await getDocs(dailyCol);

    let monthlyCard = 0;
    let monthlyCash = 0;
    let monthlyCoupon = 0;
    let monthlyOverpaid = 0;
    let monthlyAnimeGen = 0;
    let monthlyAnimeUsed = 0;
    let monthlySessions = 0;
    let hasAnyOverriddenDay = false;

    allDailiesSnap.docs.forEach(dSnap => {
      const d = dSnap.data();
      monthlyCard += Number(d['CardTotal'] || d['cardTotal'] || d['credit'] || 0);
      monthlyCash += Number(d['CashTotal'] || d['cashTotal'] || d['cash'] || 0);
      monthlyCoupon += Number(d['CouponTotal'] || d['couponTotal'] || d['coupon'] || 0);
      monthlyOverpaid += Number(d['DailyOverpaidAmount'] || d['overpaidAmount'] || d['overCharge'] || 0);
      monthlyAnimeGen += Number(d['DailyAnimefiedGenerated'] || 0);
      monthlyAnimeUsed += Number(d['DailyAnimefiedUsed'] || 0);
      monthlySessions += Number(d['DailySessions'] || d['dailySessions'] || d['totalSession'] || 0);
      if (d['isManualOverride'] === true) {
        hasAnyOverriddenDay = true;
      }
    });

    const monthlySnap = await getDoc(monthlyDocRef);
    const existingMonthly = monthlySnap.exists() ? monthlySnap.data() : {};

    const updatedMonthly = {
      ...existingMonthly,
      CardTotal: monthlyCard,
      CashTotal: monthlyCash,
      CouponTotal: monthlyCoupon,
      TotalOverpaidAmount: monthlyOverpaid,
      TotalAnimefiedGenerated: monthlyAnimeGen,
      TotalAnimefiedUsed: monthlyAnimeUsed,
      TotalSessions: monthlySessions,
      DailyRevenue: monthlyCard + monthlyCash,
      TotalRevenue: monthlyCard + monthlyCash,
      updatedAt: now,
      isManualOverride: hasAnyOverriddenDay
    };

    await setDoc(monthlyDocRef, updatedMonthly, { merge: true });
  }

  async getDeviceSessionsForDate(deviceId: string, date: string): Promise<any[]> {
    const sessionsCol = collection(this.firestore, `devices/${deviceId}/sessions/${date}/sessions`);
    const snap = await getDocs(sessionsCol);
    return snap.docs.map(d => ({ sessionId: d.id, ...d.data() }));
  }

  async getDeviceSessionsForDateRange(deviceId: string, startDateStr: string, endDateStr: string): Promise<any[]> {
    if (startDateStr === endDateStr) {
      return this.getDeviceSessionsForDate(deviceId, startDateStr);
    }

    const [sY, sM, sD] = startDateStr.split('-').map(Number);
    const [eY, eM, eD] = endDateStr.split('-').map(Number);
    const start = new Date(sY, sM - 1, sD);
    const end = new Date(eY, eM - 1, eD);

    const dates: string[] = [];
    const cur = new Date(start);
    while (cur <= end) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, '0');
      const d = String(cur.getDate()).padStart(2, '0');
      dates.push(`${y}-${m}-${d}`);
      cur.setDate(cur.getDate() + 1);
    }

    const promises = dates.map(async (dateStr) => {
      try {
        const sessionsCol = collection(this.firestore, `devices/${deviceId}/sessions/${dateStr}/sessions`);
        const snap = await getDocs(sessionsCol);
        return snap.docs.map(d => ({ sessionId: d.id, date: dateStr, ...d.data() }));
      } catch (err) {
        return [];
      }
    });

    const results = await Promise.all(promises);
    return results.flat();
  }

  async getMonthlyReport(deviceId: string, period: string): Promise<any | null> {
    const docRef = doc(this.firestore, `devices/${deviceId}/reports/${period}`);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  }

  async getPeriodOverriddenDays(period: string, brandId?: string): Promise<Set<string>> {
    const overriddenDates = new Set<string>();
    let devices = await this.getAllDevices();
    if (brandId) {
      devices = devices.filter(d => d.data?.brandId === brandId);
    }
    const promises = devices.map(async (device) => {
      try {
        const dailyCol = collection(this.firestore, `devices/${device.id}/reports/${period}/daily`);
        const snap = await getDocs(dailyCol);
        snap.docs.forEach(dSnap => {
          const d = dSnap.data();
          if (d['isManualOverride'] === true) {
            overriddenDates.add(dSnap.id);
          }
        });
      } catch (err) {
        console.warn(`Could not load daily reports for override check on ${device.id}:`, err);
      }
    });
    await Promise.all(promises);
    return overriddenDates;
  }

  async getAllDevices(): Promise<{ id: string, data: any }[]> {
    const devicesCol = collection(this.firestore, 'devices');
    const snap = await getDocs(devicesCol);
    return snap.docs
      .filter(d => d.id !== 'global')
      .map(d => ({ id: d.id, data: d.data() }));
  }

  async getAllDevicesDailyRevenue(period: string, date: string): Promise<{ deviceId: string, deviceData: any, data: any }[]> {
    const devices = await this.getAllDevices();
    const promises = devices.map(async (device) => {
      const data = await this.getDailyRevenue(device.id, period, date);
      return { deviceId: device.id, deviceData: device.data, data };
    });
    const results = await Promise.all(promises);
    return results;
  }

  async getAllDevicesDateRangeRevenue(startDateStr: string, endDateStr: string): Promise<{ deviceId: string, deviceData: any, data: any, reportDayCount: number }[]> {
    const devices = await this.getAllDevices();

    const [sY, sM, sD] = startDateStr.split('-').map(Number);
    const [eY, eM, eD] = endDateStr.split('-').map(Number);
    const start = new Date(sY, sM - 1, sD);
    const end = new Date(eY, eM - 1, eD);

    const periods: string[] = [];
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= last) {
      const p = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`;
      periods.push(p);
      cur.setMonth(cur.getMonth() + 1);
    }

    const promises = devices.map(async (device) => {
      const aggregatedData = {
        CardTotal: 0,
        CashTotal: 0,
        CouponTotal: 0,
        DailyAnimefiedGenerated: 0,
        DailyAnimefiedUsed: 0,
        DailyOverpaidAmount: 0,
        DailyRevenue: 0,
        DailySessions: 0
      };
      // Daily report docs found in range; 0 tells a missing report apart from a $0 report (KAN-284).
      let reportDayCount = 0;

      for (const period of periods) {
        try {
          const dailyCol = collection(this.firestore, `devices/${device.id}/reports/${period}/daily`);
          const snap = await getDocs(dailyCol);
          snap.docs.forEach(docSnap => {
            const dateId = docSnap.id;
            if (dateId >= startDateStr && dateId <= endDateStr) {
              reportDayCount++;
              const d = docSnap.data();
              const card = Number(d['CardTotal'] || d['cardTotal'] || d['credit'] || 0);
              const cash = Number(d['CashTotal'] || d['cashTotal'] || d['cash'] || 0);
              const coupon = Number(d['CouponTotal'] || d['couponTotal'] || d['coupon'] || 0);
              const over = Number(d['DailyOverpaidAmount'] || d['overpaidAmount'] || d['overCharge'] || 0);
              const animeGen = Number(d['DailyAnimefiedGenerated'] || 0);
              const animeUsed = Number(d['DailyAnimefiedUsed'] || 0);
              const sessions = Number(d['DailySessions'] || d['dailySessions'] || d['totalSession'] || 0);

              aggregatedData.CardTotal += card;
              aggregatedData.CashTotal += cash;
              aggregatedData.CouponTotal += coupon;
              aggregatedData.DailyOverpaidAmount += over;
              aggregatedData.DailyAnimefiedGenerated += animeGen;
              aggregatedData.DailyAnimefiedUsed += animeUsed;
              aggregatedData.DailySessions += sessions;
              aggregatedData.DailyRevenue += (card + cash);
            }
          });
        } catch (err) {
          console.warn(`Error fetching date range reports for device ${device.id}, period ${period}:`, err);
        }
      }

      return {
        deviceId: device.id,
        deviceData: device.data,
        data: aggregatedData,
        reportDayCount
      };
    });

    const results = await Promise.all(promises);
    return results;
  }

  async getAllDevicesMonthlyRevenue(period: string): Promise<{ deviceId: string, deviceData: any, data: any }[]> {
    const devices = await this.getAllDevices();
    const promises = devices.map(async (device) => {
      const data = await this.getMonthlyReport(device.id, period);
      return { deviceId: device.id, deviceData: device.data, data };
    });
    const results = await Promise.all(promises);
    return results;
  }

  // --- App Version Release Management ---

  async getAppReleases(): Promise<any[]> {
    const versionsRef = collection(this.firestore, 'photobooth_config_published/app_release/versions');
    const q = query(versionsRef, orderBy('publishedAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  async getActiveAppRelease(): Promise<any> {
    const docRef = doc(this.firestore, 'photobooth_config_published/app_release');
    const snapshot = await getDoc(docRef);
    if (snapshot.exists()) {
      return { id: snapshot.id, ...snapshot.data() };
    }
    return null;
  }

  async setActiveAppRelease(releaseData: any): Promise<void> {
    const docRef = doc(this.firestore, 'photobooth_config_published/app_release');
    const { id, ...dataToSave } = releaseData;
    await setDoc(docRef, dataToSave);
  }

  async deleteAppRelease(versionId: string, downloadUrl?: string): Promise<void> {
    if (downloadUrl) {
      try {
        const match = downloadUrl.match(/o\/(.+?)\?alt=media/);
        if (match && match[1]) {
          const storagePath = decodeURIComponent(match[1]);
          const storageRef = ref(this.storage, storagePath);
          await deleteObject(storageRef);
          console.log(`Deleted storage object: ${storagePath}`);
        }
      } catch (err) {
        console.warn(`Could not delete storage object for ${versionId}`, err);
      }
    }
    const docRef = doc(this.firestore, `photobooth_config_published/app_release/versions/${versionId}`);
    await deleteDoc(docRef);
  }

  async getAppReleaseVersion(versionId: string): Promise<any> {
    const docRef = doc(this.firestore, `photobooth_config_published/app_release/versions/${versionId}`);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return { id: snap.id, ...snap.data() };
    }
    return null;
  }

  async saveAppReleaseNote(versionId: string, releaseNote: string): Promise<void> {
    const docRef = doc(this.firestore, `photobooth_config_published/app_release/versions/${versionId}`);
    await setDoc(docRef, {
      releaseNote: releaseNote,
      releaseNoteUpdatedAt: new Date().toISOString(),
      releaseNoteUpdatedBy: this.auth.currentUser?.email || 'admin'
    }, { merge: true });
  }

  async deleteAppReleaseNote(versionId: string): Promise<void> {
    const docRef = doc(this.firestore, `photobooth_config_published/app_release/versions/${versionId}`);
    await updateDoc(docRef, {
      releaseNote: deleteField(),
      releaseNoteUpdatedAt: deleteField(),
      releaseNoteUpdatedBy: deleteField()
    });
  }

  async releaseAppToDevice(deviceId: string, versionData: any, triggerRestart: boolean = false, forceUpdate: boolean = false): Promise<void> {
    const deviceDocRef = doc(this.firestore, 'devices', deviceId);
    await setDoc(deviceDocRef, {
      appUpdateNeeded: true,
      forceAppUpdate: forceUpdate,
      targetAppVersion: versionData.version,
      appReleaseTargetedAt: new Date(),
      appReleaseTargetedBy: this.auth.currentUser?.email || 'admin'
    }, { merge: true });

    if (triggerRestart) {
      await this.sendRemoteCommand(deviceId, 'RESTART_APP', {
        reason: `Release App Update v${versionData.version}${forceUpdate ? ' (Forced)' : ''}`,
        source: 'ERP_APP_RELEASE'
      });
    }
  }

  /** KAN-436: active PhotoLavie devices a release may write to; Konobox, soft-deleted and `global` are reported as skipped. */
  async getAppReleaseTargets(candidateIds?: string[]): Promise<AppReleaseTargets> {
    const [devicesSnap, storesSnap, brandsSnap] = await Promise.all([
      getDocs(collection(this.firestore, 'devices')),
      getDocs(collection(this.firestore, 'stores')),
      getDocs(collection(this.firestore, 'brands'))
    ]);
    const rows = (snap: any) => snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
    return partitionAppReleaseTargets(rows(devicesSnap), rows(storesSnap), rows(brandsSnap), candidateIds);
  }

  async releaseAppToAllDevices(versionData: any, triggerRestart: boolean = false, forceUpdate: boolean = false): Promise<{ successCount: number, failCount: number, skippedCount: number }> {
    // 1. Update Global Active Release (unchanged by KAN-436)
    await this.setActiveAppRelease(versionData);

    // 2. Release only to active PhotoLavie devices
    const { eligibleIds, skipped } = await this.getAppReleaseTargets();
    let successCount = 0;
    let failCount = 0;

    const promises = eligibleIds.map(async (id) => {
      try {
        await this.releaseAppToDevice(id, versionData, triggerRestart, forceUpdate);
        successCount++;
      } catch (e) {
        console.error(`Failed to trigger release on device ${id}`, e);
        failCount++;
      }
    });

    await Promise.all(promises);
    return { successCount, failCount, skippedCount: skipped.length };
  }

  async releaseAppToSelectedDevices(requestedIds: string[], versionData: any, triggerRestart: boolean = false, forceUpdate: boolean = false): Promise<{ successCount: number, failCount: number, skippedCount: number }> {
    let successCount = 0;
    let failCount = 0;
    const { eligibleIds: deviceIds, skipped } = await this.getAppReleaseTargets(requestedIds);

    const promises = deviceIds.map(async (deviceId) => {
      try {
        await this.releaseAppToDevice(deviceId, versionData, triggerRestart, forceUpdate);
        successCount++;
      } catch (e) {
        console.error(`Failed to trigger release on device ${deviceId}`, e);
        failCount++;
      }
    });

    await Promise.all(promises);
    return { successCount, failCount, skippedCount: skipped.length };
  }
}

